import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../auth/contexts/AuthContext';
import { db } from '../../../shared/storage/firebase';
import { addDoc, arrayUnion, collection, deleteDoc, doc, getDocs, onSnapshot, query, serverTimestamp, where, writeBatch } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { slugify } from '../../../shared/utils/slugify';
import HtmlDocumentEditor from '../../../shared/ui/HtmlDocumentEditor';
import {
  createPartnerContact,
  createPartnerLocation,
  createPartnerNote,
  createPartnerRequirement,
  deletePartner,
  deletePartnerContact,
  deletePartnerRequirement,
  getPartner,
  listPartnerCalendarEntries,
  listPartnerContacts,
  listOrganizationSeasons,
  listPartnerDocuments,
  listPartnerLocations,
  listPartnerNotes,
  listPartnerRequirements,
  listPartnerSeasons,
  listPartnerSquads,
  listPartnerTeacherClearances,
  savePartnerSeason,
  savePartnerTeacherClearance,
  updatePartner,
  updatePartnerContact,
  updatePartnerLocation,
  updatePartnerRequirement,
  updatePartnerSquad,
  updatePartnerSquadSeason,
} from '../services/partnerRepository';
import { geocodeLocationAddress } from '../services/locationGeocode';
import { effectiveRegistrationLink, summarizeTeacherClearance } from '../utils/model';
import {
  derivePartnerSeason,
  seasonHealthLabel,
  seasonPhaseLabel,
  type DerivedPartnerSeason,
} from '../utils/seasonDerived';
import { removeLocationCampusPhoto, uploadLocationCampusPhoto, uploadPartnerBrandingImage, uploadPartnerDocument } from '../services/partnerFiles';
import { loadPartnerParentDirectory, type PartnerParentDirectory } from '../services/partnerDirectory';
import {
  derivePartnerGeography,
  filterAndRankTeachers,
  globalEvidenceForRequirement,
  listActivePartnerTeacherCandidates,
  partnerRequirementDisplayState,
  teacherProximity,
  type PartnerTeacherCandidate,
} from '../services/partnerTeacherDiscovery';
import type {
  Partner,
  PartnerCalendarEntry,
  PartnerContact,
  PartnerLocation,
  OrganizationSeason,
  PartnerRequirement,
  PartnerRequirementType,
  RequirementCompletionStatus,
  PartnerDocumentKind,
  PartnerSeason,
  PartnerTeacherClearance,
  ScopedDocument,
  ScopedNote,
} from '../types';
import type { ContactRole, Invoice, Squad } from '../../../types';
import { listProgramTerms, programTermForDate, programTermLabelFromId } from '../../../shared/terms/programTerms';
import { computeTotalClassesFromSchedule, resolveSquadTermId } from '../../../shared/billing/squadInvoiceRollup';
import { deriveInvoiceStatus } from '../../../shared/billing/invoiceStatus';
import { ORG_DATE_SOURCE, organizationSeasonForTerm } from '../utils/organizationSeasons';
import { inferSeasonFromStartDate } from '../utils/seasonAssignment';
import { isNonStripePaymentMode, publicLocationPageUrl, publicPartnerPageUrl } from '../utils/publicWebsite';
import { comparePartnerInvoices } from '../utils/invoiceSort';
import { inheritedNoClassDatesForLocation } from '../utils/partnerCalendarEntries';
import PartnerCalendarTab, { type ConvertEventOptions } from './PartnerCalendarTab';
import PartnerMarketingTab from './PartnerMarketingTab';
import { marketingSummary, seasonMarketingDeliverables } from '../utils/partnerMarketing';
import PhoneCallButton from './PhoneCallButton';
import ConfirmationModal from '../../squads/components/ConfirmationModal';
import { shouldConfirmArchiveInsteadOfCancel } from '../../squads/utils/squadLifecycle';
import { DEFAULT_TZ, formatTimeInZone } from '../../../shared/utils/timezone';
import ManageSquadUsersModal from '../../admin/components/ManageSquadUsersModal';
import { syncSquadCalendarFromDetails } from '../../squads/utils/calendarSync';
import { effectiveScheduleDate, normalizedSingleDaySchedule } from '../../squads/utils/singleDaySchedule';
import '../styles/PartnerDetailPage.css';

type Tab =
  | 'overview'
  | 'locations'
  | 'contacts'
  | 'requirements'
  | 'billing'
  | 'seasons'
  | 'marketing'
  | 'notes'
  | 'parents'
  | 'squads'
  | 'calendar';

const tabs: Array<{ id: Tab; label: string }> = [
  { id: 'overview', label: 'Overview' },
  { id: 'locations', label: 'Locations' },
  { id: 'squads', label: 'Squads' },
  { id: 'calendar', label: 'Calendar' },
  { id: 'seasons', label: 'Seasons' },
  { id: 'marketing', label: 'Marketing' },
  { id: 'contacts', label: 'Contacts' },
  { id: 'requirements', label: 'Requirements & Teachers' },
  { id: 'billing', label: 'Billing & Documents' },
  { id: 'notes', label: 'Notes' },
  { id: 'parents', label: 'Parents' },
];

const organizationTypeLabels: Record<string, string> = {
  traditional_public: 'School',
  public_charter_school: 'Public charter',
  private_school: 'Private school',
  community_organization: 'Community organization',
  school_district: 'School district',
  park_district: 'Park district',
  other: 'Other',
};

const contactRoleLabels: Record<string, string> = {
  primary: 'Main POC',
  principal: 'Principal / CEO',
  pta_president: 'PTO / PTA',
  marketing_director: 'Marketing',
  afterschool_director: 'Afterschool director',
  finance: 'Finance',
  operations: 'Operations / secretary',
  other: 'Other',
};

function text(value: unknown): string {
  return String(value ?? '').trim();
}

function humanize(value: unknown): string {
  const clean = text(value);
  if (!clean) return 'Not set';
  return clean.replace(/_/g, ' ').replace(/\b\w/g, (character) => character.toUpperCase());
}

const NOTE_HTML_TAGS = new Set(['P', 'BR', 'STRONG', 'B', 'EM', 'I', 'UL', 'OL', 'LI', 'H1', 'H2', 'H3']);

function escapeNoteText(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function notePlainText(value: string): string {
  if (!value) return '';
  const parsed = new DOMParser().parseFromString(value, 'text/html');
  return (parsed.body.textContent || '').replace(/\s+/g, ' ').trim();
}

function sanitizeNoteHtml(value: string): string {
  const parsed = new DOMParser().parseFromString(value, 'text/html');
  const clean = (node: Node): string => {
    if (node.nodeType === Node.TEXT_NODE) return escapeNoteText(node.textContent || '');
    if (!(node instanceof HTMLElement)) return '';
    const children = Array.from(node.childNodes).map(clean).join('');
    if (!NOTE_HTML_TAGS.has(node.tagName)) return children;
    const tag = node.tagName.toLowerCase();
    return tag === 'br' ? '<br>' : `<${tag}>${children}</${tag}>`;
  };
  return Array.from(parsed.body.childNodes).map(clean).join('').trim();
}

function noteDisplayHtml(value: string): string {
  if (!/<[a-z][\s\S]*>/i.test(value)) {
    return `<p>${escapeNoteText(value).replace(/\n/g, '<br>')}</p>`;
  }
  return sanitizeNoteHtml(value);
}

function formatDate(value: unknown): string {
  if (!value) return '';
  const candidate = value as { toDate?: () => Date };
  const date = typeof candidate.toDate === 'function' ? candidate.toDate() : new Date(value as string | number | Date);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(date);
}

function formatScheduleDate(value: unknown): string {
  const dateKey = String(value || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateKey)) return '';
  const [, month, day] = dateKey.split('-');
  return `${Number(month)}/${Number(day)}`;
}

function formatClockTime(value: unknown): string {
  const match = text(value).match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return text(value);
  const hour = Number(match[1]);
  const minute = match[2];
  if (hour > 23) return text(value);
  return `${hour % 12 || 12}:${minute} ${hour >= 12 ? 'PM' : 'AM'}`;
}

function timeZoneAbbreviation(timeZone: string, dateValue?: string): string {
  try {
    const date = dateValue
      ? new Date(`${dateValue}T12:00:00Z`)
      : new Date();
    return new Intl.DateTimeFormat('en-US', {
      timeZone,
      timeZoneName: 'short',
    }).formatToParts(date).find((part) => part.type === 'timeZoneName')?.value || timeZone;
  } catch {
    return timeZone;
  }
}

function formatSquadSchedule(squad: Squad, partnerTimeZone: string): { schedule: string; timeZone: string } {
  const timeZone = squad.timezone || partnerTimeZone || 'America/Chicago';
  const dayLabels: Record<string, string> = {
    MON: 'Mon',
    TUE: 'Tue',
    WED: 'Wed',
    THU: 'Thu',
    FRI: 'Fri',
    SAT: 'Sat',
    SUN: 'Sun',
  };
  const day = dayLabels[text(squad.schedule?.dayOfWeek).toUpperCase()] || text(squad.schedule?.dayOfWeek);
  const start = formatClockTime(squad.schedule?.startTime);
  const end = formatClockTime(squad.schedule?.endTime);
  const zone = timeZoneAbbreviation(timeZone, squad.schedule?.mainDate || squad.schedule?.startDate);
  const timeRange = start && end
    ? `${start.replace(/ (AM|PM)$/, '')}–${end}`
    : start || end;
  const schedule = [day, timeRange ? `${timeRange} ${zone}` : ''].filter(Boolean).join(' · ')
    || (squad.classTime ? `${squad.classTime} · ${zone}` : 'Not scheduled');
  return { schedule, timeZone };
}

function inferredMetro(locations: PartnerLocation[]): string {
  for (const location of locations) {
    const parts = String(location.address || '').split(',').map((part) => part.trim()).filter(Boolean);
    if (parts.length >= 3) return parts[parts.length - 2];
  }
  return '';
}

export default function PartnerDetailPage(): React.ReactElement {
  const { partnerId = '' } = useParams<{ partnerId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentUser } = useAuth();
  const actorId = currentUser?.uid || null;
  const requestedTab = searchParams.get('tab') as Tab | null;
  const tab: Tab = tabs.some((item) => item.id === requestedTab) ? requestedTab as Tab : 'overview';
  const [partner, setPartner] = useState<Partner | null>(null);
  const [locations, setLocations] = useState<PartnerLocation[]>([]);
  const [contacts, setContacts] = useState<PartnerContact[]>([]);
  const [requirements, setRequirements] = useState<PartnerRequirement[]>([]);
  const [clearances, setClearances] = useState<PartnerTeacherClearance[]>([]);
  const [seasons, setSeasons] = useState<PartnerSeason[]>([]);
  const [notes, setNotes] = useState<ScopedNote[]>([]);
  const [documents, setDocuments] = useState<ScopedDocument[]>([]);
  const [squads, setSquads] = useState<Squad[]>([]);
  const [calendarEntries, setCalendarEntries] = useState<PartnerCalendarEntry[]>([]);
  const [newSquadLocationId, setNewSquadLocationId] = useState('');
  const [managedSquad, setManagedSquad] = useState<Squad | null>(null);
  const [parentDirectory, setParentDirectory] = useState<PartnerParentDirectory | null>(null);
  const [parentDirectoryLoading, setParentDirectoryLoading] = useState(false);
  const [parentDirectoryAttempted, setParentDirectoryAttempted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoLoadFailed, setLogoLoadFailed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async (options?: { silent?: boolean }) => {
    if (!partnerId) return;
    const silent = options?.silent === true;
    // Inline edits call load({ silent: true }) so we refresh data without
    // unmounting the page into the full-screen "Loading partner…" state.
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    try {
      const [
        partnerValue,
        locationValues,
        contactValues,
        requirementValues,
        clearanceValues,
        seasonValues,
        noteValues,
        documentValues,
        squadValues,
        calendarEntryValues,
      ] = await Promise.all([
        getPartner(partnerId),
        listPartnerLocations(partnerId),
        listPartnerContacts(partnerId),
        listPartnerRequirements(partnerId),
        listPartnerTeacherClearances(partnerId),
        listPartnerSeasons(partnerId),
        listPartnerNotes(partnerId),
        listPartnerDocuments(partnerId),
        listPartnerSquads(partnerId),
        // Non-fatal: a calendar-entries failure (e.g. rules not deployed yet)
        // must not take down the whole partner page.
        listPartnerCalendarEntries(partnerId).catch((entriesError) => {
          console.warn('Could not load partner calendar entries:', entriesError);
          return [];
        }),
      ]);
      setPartner(partnerValue);
      setLocations(locationValues);
      setContacts(contactValues);
      setRequirements(requirementValues);
      setClearances(clearanceValues);
      setSeasons(seasonValues);
      setNotes(noteValues);
      setDocuments(documentValues);
      setSquads(squadValues);
      setCalendarEntries(calendarEntryValues);
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Failed to load partner.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [partnerId]);

  const reloadQuietly = useCallback(() => load({ silent: true }), [load]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (tab !== 'parents' || !partnerId || parentDirectoryAttempted) return;
    setParentDirectoryAttempted(true);
    setParentDirectoryLoading(true);
    loadPartnerParentDirectory(partnerId)
      .then(setParentDirectory)
      .catch((nextError) => setError(nextError instanceof Error ? nextError.message : 'Failed to load parent directory.'))
      .finally(() => setParentDirectoryLoading(false));
  }, [tab, partnerId, parentDirectoryAttempted]);

  useEffect(() => {
    if (!message) return;
    const timer = window.setTimeout(() => setMessage(null), 3500);
    return () => window.clearTimeout(timer);
  }, [message]);

  useEffect(() => {
    setLogoLoadFailed(false);
  }, [partner?.branding?.logoUrl]);

  const savePartnerPatch = async (patch: Partial<Partner>): Promise<boolean> => {
    if (!partner) return false;
    setSaving(true);
    setError(null);
    try {
      await updatePartner(partner.id, patch, actorId);
      setPartner((current) => current ? { ...current, ...patch } : current);
      setMessage('Saved.');
      return true;
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Save failed.');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const togglePublicVisibility = async (isPublic: boolean) => {
    if (!partner) return;
    setSaving(true);
    setError(null);
    try {
      await updatePartner(partner.id, { isPublic }, actorId);
      setPartner((current) => current ? { ...current, isPublic } : current);
      setMessage(isPublic ? 'Partner is now visible on the public website.' : 'Partner is hidden from the public website.');
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Could not update public visibility.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePartner = async () => {
    if (!partner) return;
    const confirmed = await window.appConfirm(
      `Delete “${partner.name}”? This removes the partner record and its locations from the website. Squads are not deleted. This cannot be undone.`
    );
    if (!confirmed) return;
    setDeleting(true);
    setError(null);
    try {
      await deletePartner(partner.id);
      navigate('/app/admin', { state: { adminTab: 'users', adminUserTab: 'partners' } });
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Unable to delete partner.');
      setDeleting(false);
    }
  };

  const uploadLogo = async (file: File, input: HTMLInputElement) => {
    if (!partner) return;
    setUploadingLogo(true);
    setError(null);
    try {
      const logoUrl = await uploadPartnerBrandingImage({
        partnerId: partner.id,
        file,
        kind: 'logo',
        actorId,
      });
      setPartner((current) => current
        ? { ...current, branding: { ...current.branding, logoUrl } }
        : current);
      setLogoLoadFailed(false);
      setMessage('Partner logo updated.');
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Logo upload failed.');
    } finally {
      setUploadingLogo(false);
      input.value = '';
    }
  };

  const parentFacts = useMemo(() => {
    const parentRows = squads.flatMap((squad) => Array.isArray(squad.enrolledParentNamesAndEmails) ? squad.enrolledParentNamesAndEmails : []);
    const linkedStudentIds = new Set(squads.flatMap((squad) => Array.isArray(squad.studentIds) ? squad.studentIds : []));
    return {
      legacyParentRows: Array.from(new Set(parentRows.map(text).filter(Boolean))),
      linkedStudentCount: linkedStudentIds.size,
    };
  }, [squads]);

  const nextAction = useMemo(() => {
    if (!partner) return 'Finish partner setup';
    const checks = [
      Boolean(partner.name && partner.organizationType && (partner.metro || inferredMetro(locations))),
      locations.length > 0,
      contacts.length > 0,
      squads.length > 0,
      requirements.length > 0,
      seasons.length > 0,
      Boolean(partner.billingDefaults?.billingContactId || documents.length > 0),
    ];
    return !checks[0] ? 'Complete the partner profile'
      : !checks[1] ? 'Add the first location'
      : !checks[2] ? 'Add a primary contact'
      : !checks[3] ? 'Create or link a squad'
      : !checks[4] ? 'Define teacher requirements'
      : !checks[5] ? 'Set up the current season'
      : !checks[6] ? 'Complete billing setup'
      : 'Review open squads and clearances';
  }, [contacts.length, documents.length, locations, partner, requirements.length, seasons.length, squads.length]);

  const activeSeason = useMemo(() => (
    seasons
      .slice()
      .sort((a, b) => text(b.registrationStartDate).localeCompare(text(a.registrationStartDate)))[0]
  ), [seasons]);

  const selectTab = (nextTab: Tab) => {
    const nextParams = new URLSearchParams(searchParams);
    if (nextTab === 'overview') nextParams.delete('tab');
    else nextParams.set('tab', nextTab);
    setSearchParams(nextParams);
    setError(null);
    setMessage(null);
  };

  const createSquad = async (termId: string, title: string, locationId: string, options?: { scheduleKind?: 'recurring' | 'single_day'; singleDayType?: 'class' | 'demo'; demoType?: Squad['demoType']; mainDate?: string; startTime?: string; endTime?: string }): Promise<string> => {
    if (!currentUser) throw new Error('Sign in is required.');
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    const code = Array.from({ length: 5 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
    const squadRef = doc(collection(db, 'squads'));
    const nowMs = Date.now();
    const location = locations.find((item) => item.id === locationId);
    const inheritedPriceCents = Number(partner?.billingDefaults?.defaultProgramPriceCents);
    const defaultMinEnroll = Number.isFinite(inheritedPriceCents) && inheritedPriceCents > 0
      ? Math.floor(10000 / inheritedPriceCents) + 1
      : undefined;
    const batch = writeBatch(db);
    const squadData = {
      id: squadRef.id,
      title: title.trim(),
      code,
      curriculumId: 'intro-ai-kids-2026',
      partnerId,
      locationId,
      seasonId: termId,
      termId,
      term: programTermLabelFromId(termId),
      deliveryMode: 'in_person',
      scheduleKind: options?.scheduleKind || 'recurring',
      ...(options?.scheduleKind === 'single_day' ? {
        singleDayType: options.singleDayType || 'class',
        ...(options.singleDayType === 'demo' && options.demoType ? { demoType: options.demoType } : {}),
        schedule: normalizedSingleDaySchedule({
          mainDate: options.mainDate || '',
          startTime: options.startTime || '09:00',
          endTime: options.endTime || '10:00',
        }),
        totalClasses: 1,
      } : {
        // Recurring squads inherit the partner's no-class days from day one;
        // the rest of the schedule is filled in on the squad details page.
        schedule: { inheritedExceptions: inheritedNoClassDatesForLocation(calendarEntries, locationId) },
      }),
      timezone: location?.timezone || partner?.timezone || 'America/Chicago',
      capacity: 12,
      ...(defaultMinEnroll ? { minEnroll: defaultMinEnroll } : {}),
      paymentMode: partner?.billingDefaults?.paymentMode || 'stripe',
      ...(() => {
        const defaultPrice = Number(partner?.billingDefaults?.defaultProgramPriceCents);
        if (!Number.isFinite(defaultPrice) || defaultPrice < 0) return {};
        const paymentMode = partner?.billingDefaults?.paymentMode || 'stripe';
        return paymentMode === 'school_flat_fee'
          ? { flatFeePerClassCents: Math.round(defaultPrice), priceCents: Math.round(defaultPrice), priceSource: 'partner_default' }
          : { tuitionPerStudentPerClassCents: Math.round(defaultPrice), priceCents: Math.round(defaultPrice), priceSource: 'partner_default' };
      })(),
      // Published demos enter the teacher claim marketplace unassigned.
      teacherIds: options?.singleDayType === 'demo' ? [] : [currentUser.uid],
      studentIds: [],
      status: 'draft',
      visibility: 'public',
      notes: '',
      createdAt: serverTimestamp(),
    } as unknown as Squad;
    batch.set(squadRef, squadData);
    batch.set(doc(db, 'partnerSeasonFlows', `${partnerId}__${termId}`), {
      partnerId,
      seasonId: termId,
      locationIds: arrayUnion(locationId),
      squadIds: arrayUnion(squadRef.id),
      legacyOpsFlowIds: arrayUnion(squadRef.id),
      pipelineStage: 'lead',
      updatedAt: nowMs,
      updatedBy: currentUser.uid,
    }, { merge: true });
    await batch.commit();
    if (options?.scheduleKind === 'single_day' && options.mainDate) {
      await syncSquadCalendarFromDetails(squadData);
    }
    await reloadQuietly();
    setMessage(`Squad “${title}” created.`);
    return squadRef.id;
  };

  const convertEntryToSquad = async (entry: PartnerCalendarEntry, options: ConvertEventOptions): Promise<string> => {
    const termId = programTermForDate(new Date(`${entry.startDate}T12:00:00`)).id;
    return createSquad(termId, options.title, options.locationId, {
      scheduleKind: 'single_day',
      singleDayType: options.singleDayType,
      demoType: options.singleDayType === 'demo' ? options.demoType : undefined,
      mainDate: entry.startDate,
      startTime: options.startTime,
      endTime: options.endTime,
    });
  };

  const deleteSquad = async (squad: Squad) => {
    const confirmed = await window.appConfirm(`Delete “${squad.title || 'this squad'}”? This also removes its public listing and calendar events. This cannot be undone.`);
    if (!confirmed) return;
    setError(null);
    try {
      const eventSnapshot = await getDocs(query(collection(db, 'squadEvents'), where('squadId', '==', squad.id)));
      let batch = writeBatch(db);
      let pending = 0;
      for (const eventDoc of eventSnapshot.docs) {
        batch.delete(eventDoc.ref);
        pending += 1;
        if (pending >= 450) {
          await batch.commit();
          batch = writeBatch(db);
          pending = 0;
        }
      }
      if (pending > 0) await batch.commit();
      await deleteDoc(doc(db, 'squads', squad.id));
      // The public projection may be server-managed and therefore reject direct client deletes.
      await Promise.allSettled([deleteDoc(doc(db, 'publicSquads', squad.id))]);
      setSquads((current) => current.filter((item) => item.id !== squad.id));
      setMessage(`Squad “${squad.title || squad.id}” deleted.`);
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Unable to delete squad.');
      await reloadQuietly();
    }
  };

  if (loading) return <main className="partner-detail-page"><div className="partner-loading">Loading partner…</div></main>;
  if (!partner) return <main className="partner-detail-page"><div className="partner-loading">Partner not found.</div></main>;
  const metro = partner.metro || inferredMetro(locations);

  return (
    <main className="partner-detail-page">
      <button
        type="button"
        onClick={() => navigate('/app/admin', { state: { adminTab: 'users', adminUserTab: 'partners' } })}
        className="partner-back-button"
      >
        ← Partners
      </button>
      <div className="partner-hero">
        <div className="partner-hero__identity">
          <label className={`partner-hero__logo-upload${uploadingLogo ? ' partner-hero__logo-upload--busy' : ''}`} title="Upload a new partner logo">
            <input
              type="file"
              accept="image/*"
              disabled={uploadingLogo}
              aria-label={`Upload a new logo for ${partner.name}`}
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) void uploadLogo(file, event.target);
              }}
            />
            <span className="partner-hero__logo">
              {partner.branding?.logoUrl && !logoLoadFailed
                ? <img src={partner.branding.logoUrl} alt={`${partner.name} logo`} onError={() => setLogoLoadFailed(true)} />
                : partner.name.slice(0, 1).toUpperCase()}
              <span className="partner-hero__logo-action" aria-hidden="true">{uploadingLogo ? 'Uploading…' : 'Change'}</span>
            </span>
          </label>
          <div>
            <div className="partner-hero__title-row">
              <h1>{partner.name}</h1>
              {String(partner.billingDefaults?.paymentMode || 'stripe').trim().toLowerCase() === 'stripe' && (
                <span className="partner-stripe-badge partner-stripe-badge--hero" title="Stripe school — parents pay directly" aria-label="Stripe school — parents pay directly">💰</span>
              )}
            </div>
            <p className="partner-hero__meta">
              {organizationTypeLabels[partner.organizationType || ''] || humanize(partner.organizationType || 'Unclassified')} · {metro || 'Metro not set'}
            </p>
          </div>
        </div>
        <div className="partner-hero__summary">
          <span className={`partner-status-badge partner-status-badge--${partner.active === false ? 'inactive' : 'active'}`}>
            {partner.active === false ? 'Inactive' : 'Active'}
          </span>
          <div className="partner-hero__facts" aria-label="Partner setup summary">
            <button type="button" onClick={() => selectTab('locations')}>{locations.length} {locations.length === 1 ? 'location' : 'locations'}</button>
            <span aria-hidden="true">·</span>
            <button type="button" onClick={() => selectTab('squads')}>{squads.length} {squads.length === 1 ? 'squad' : 'squads'}</button>
            <span aria-hidden="true">·</span>
            <button type="button" onClick={() => selectTab('seasons')}>Latest: {activeSeason?.label || activeSeason?.termId || 'Not set'}</button>
          </div>
        </div>
      </div>
      <p className="partner-next-action"><strong>Next action:</strong> {nextAction}</p>

      <div className="partner-tab-bar" role="tablist" aria-label="Partner details">
        {tabs.map((item) => (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={tab === item.id}
            onClick={() => selectTab(item.id)}
          >
            {item.label}
          </button>
        ))}
      </div>

      {error && <div role="alert" className="partner-notice partner-notice--error"><span>{error}</span><button type="button" onClick={() => setError(null)}>Dismiss</button></div>}
      {message && <div role="status" className="partner-notice partner-notice--success"><span>{message}</span></div>}

      {tab === 'overview' && (
        <OverviewSection
          partner={partner}
          locations={locations}
          inferredMetro={metro}
          saving={saving}
          deleting={deleting}
          actorId={actorId}
          onSave={savePartnerPatch}
          onTogglePublic={togglePublicVisibility}
          onDeletePartner={handleDeletePartner}
          onLocationsChanged={reloadQuietly}
        />
      )}
      {tab === 'locations' && (
        <LocationsSection partner={partner} locations={locations} actorId={actorId} onChanged={reloadQuietly} />
      )}
      {tab === 'calendar' && (
        <PartnerCalendarTab
          partner={partner}
          squads={squads}
          locations={locations}
          entries={calendarEntries}
          actorId={actorId}
          onChanged={reloadQuietly}
          onOpenSquad={(squadId) => navigate(`/app/admin/squads/${squadId}`)}
          onConvertEventToSquad={convertEntryToSquad}
        />
      )}
      {tab === 'contacts' && (
        <ContactsSection partner={partner} contacts={contacts} locations={locations} actorId={actorId} onChanged={reloadQuietly} />
      )}
      {tab === 'requirements' && (
        <RequirementsSection partner={partner} requirements={requirements} documents={documents} clearances={clearances} locations={locations} actorId={actorId} onChanged={reloadQuietly} />
      )}
      {tab === 'billing' && (
        <BillingSection partner={partner} squads={squads} contacts={contacts} documents={documents} saving={saving} actorId={actorId} onSave={savePartnerPatch} onChanged={reloadQuietly} onManageContacts={() => selectTab('contacts')} />
      )}
      {tab === 'seasons' && (
        <SeasonsSection
          partner={partner}
          seasons={seasons}
          locations={locations}
          squads={squads}
          actorId={actorId}
          onChanged={reloadQuietly}
          onCreateSquad={createSquad}
        />
      )}
      {tab === 'marketing' && (
        <PartnerMarketingTab partner={partner} seasons={seasons} squads={squads} contacts={contacts} locations={locations} actorId={actorId} onChanged={reloadQuietly} />
      )}
      {tab === 'notes' && (
        <NotesSection partner={partner} notes={notes} actorId={actorId} onChanged={reloadQuietly} />
      )}
      {tab === 'parents' && (
        <ParentsSection
          partner={partner}
          parentDirectory={parentDirectory}
          parentDirectoryLoading={parentDirectoryLoading}
          legacyParentRows={parentFacts.legacyParentRows}
          linkedStudentCount={parentFacts.linkedStudentCount}
        />
      )}
      {tab === 'squads' && (
        <SquadsSection
          partner={partner}
          squads={squads}
          locations={locations}
          seasons={seasons}
          actorId={actorId}
          newSquadLocationId={newSquadLocationId}
          onLocationChange={setNewSquadLocationId}
          onSquadPatched={(squadId, patch) => {
            setSquads((current) => current.map((squad) => (
              squad.id === squadId ? { ...squad, ...patch } : squad
            )));
          }}
          onLaunchDashboard={(squadId) => navigate(`/app/squads/${squadId}`)}
          onOpenSquad={(squadId) => navigate(`/app/admin/squads/${squadId}`)}
          onManageUsers={setManagedSquad}
          onDelete={(squad) => void deleteSquad(squad)}
          onCreate={createSquad}
        />
      )}
      <ManageSquadUsersModal
        isOpen={Boolean(managedSquad)}
        squad={managedSquad}
        onClose={() => setManagedSquad(null)}
        onSquadUpdate={(updatedSquad) => {
          setSquads((current) => current.map((item) => item.id === updatedSquad.id ? updatedSquad : item));
          setManagedSquad(updatedSquad);
        }}
      />
    </main>
  );
}

function SquadsSection(props: {
  partner: Partner;
  squads: Squad[];
  locations: PartnerLocation[];
  seasons: PartnerSeason[];
  actorId: string | null;
  newSquadLocationId: string;
  onLocationChange: (value: string) => void;
  onSquadPatched: (squadId: string, patch: Partial<Squad>) => void;
  onLaunchDashboard: (squadId: string) => void;
  onOpenSquad: (squadId: string) => void;
  onManageUsers: (squad: Squad) => void;
  onDelete: (squad: Squad) => void;
  onCreate: (termId: string, title: string, locationId: string, options?: { scheduleKind?: 'recurring' | 'single_day'; singleDayType?: 'class' | 'demo'; demoType?: Squad['demoType']; mainDate?: string; startTime?: string; endTime?: string }) => Promise<string>;
}): React.ReactElement {
  const {
    partner,
    squads,
    locations,
    seasons,
    actorId,
    newSquadLocationId,
    onLocationChange,
    onSquadPatched,
    onLaunchDashboard,
    onOpenSquad,
    onManageUsers,
    onDelete,
    onCreate,
  } = props;
  const [statusFilter, setStatusFilter] = useState('active');
  const [creating, setCreating] = useState(false);
  const [createTitle, setCreateTitle] = useState('');
  const [createSeasonId, setCreateSeasonId] = useState('');
  const [createScheduleKind, setCreateScheduleKind] = useState<'recurring' | 'single_day'>('recurring');
  const [createSingleDayType, setCreateSingleDayType] = useState<'class' | 'demo'>('class');
  const [createDemoType, setCreateDemoType] = useState<Squad['demoType']>();
  const [createMainDate, setCreateMainDate] = useState('');
  const [createStartTime, setCreateStartTime] = useState('09:00');
  const [createEndTime, setCreateEndTime] = useState('10:00');
  const [createBusy, setCreateBusy] = useState(false);
  const [openMenuSquadId, setOpenMenuSquadId] = useState<string | null>(null);
  const [editingTitleSquadId, setEditingTitleSquadId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [savingCell, setSavingCell] = useState<string | null>(null);
  const [editError, setEditError] = useState<string | null>(null);
  const [archiveConfirmSquad, setArchiveConfirmSquad] = useState<Squad | null>(null);
  const visibleSquads = squads.filter((squad) => (
    statusFilter === 'all'
      || (statusFilter === 'active' ? !['archived', 'canceled'].includes(squad.status || '') : squad.status === statusFilter)
  ));
  useEffect(() => {
    if (!createSeasonId && seasons.length) {
      setCreateSeasonId(seasons[0].termId || seasons[0].id);
    }
  }, [createSeasonId, seasons]);

  const submitCreate = async (event: React.FormEvent) => {
    event.preventDefault();
    const title = createTitle.trim();
    if (!title || !newSquadLocationId || !createSeasonId) return;
    setCreateBusy(true);
    setEditError(null);
    try {
      await onCreate(createSeasonId, title, newSquadLocationId, {
        scheduleKind: createScheduleKind,
        ...(createScheduleKind === 'single_day' ? { singleDayType: createSingleDayType, demoType: createDemoType, mainDate: createMainDate, startTime: createStartTime, endTime: createEndTime } : {}),
      });
      setCreateTitle('');
      setCreating(false);
    } catch (nextError) {
      setEditError(nextError instanceof Error ? nextError.message : 'Unable to create squad.');
    } finally {
      setCreateBusy(false);
    }
  };
  const saveSquadPatch = async (
    squadId: string,
    field: string,
    patch: Parameters<typeof updatePartnerSquad>[1],
  ) => {
    try {
      setSavingCell(`${squadId}:${field}`);
      setEditError(null);
      await updatePartnerSquad(squadId, patch, actorId);
      onSquadPatched(squadId, patch);
    } catch (nextError) {
      setEditError(nextError instanceof Error ? nextError.message : 'Failed to update squad.');
    } finally {
      setSavingCell(null);
    }
  };
  const saveSquadStatus = async (squadId: string, status: Squad['status']) => {
    await saveSquadPatch(
      squadId,
      'status',
      status === 'published' ? { status, visibility: 'public' } : { status },
    );
  };
  const saveTitle = async (squad: Squad) => {
    const nextTitle = editingTitle.trim();
    if (!nextTitle) {
      setEditError('Squad name cannot be empty.');
      return;
    }
    if (nextTitle !== squad.title) await saveSquadPatch(squad.id, 'title', { title: nextTitle });
    setEditingTitleSquadId(null);
  };
  const saveSeasonAssignment = async (squad: Squad, nextTermId: string) => {
    if (!nextTermId) return;
    const previousTermId = squad.termId || squad.seasonId || '';
    const nextSeason = seasons.find((season) => season.termId === nextTermId || season.id === nextTermId);
    const nextLabel = nextSeason?.label || programTermLabelFromId(nextTermId);
    try {
      setSavingCell(`${squad.id}:season`);
      setEditError(null);
      await updatePartnerSquadSeason(partner.id, squad.id, previousTermId, nextTermId, nextLabel, squad.locationId || '', actorId);
      onSquadPatched(squad.id, { seasonId: nextTermId, termId: nextTermId, term: nextLabel });
    } catch (nextError) {
      setEditError(nextError instanceof Error ? nextError.message : 'Failed to assign squad season.');
    } finally {
      setSavingCell(null);
    }
  };

  return (
    <section className="partner-billing-workspace">
      <div className="partner-section-header">
        <div>
          <h2>Squads</h2>
          <p>Programs linked to this partner. Select or click a field in the table to update it directly.</p>
        </div>
        <div className="partner-actions partner-actions--compact">
          <select aria-label="Filter squads by status" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
            <option value="active">Active work</option>
            <option value="published">Published</option>
            <option value="draft">Draft</option>
            <option value="archived">Archived</option>
            <option value="canceled">Canceled</option>
            <option value="all">All statuses</option>
          </select>
          <button type="button" className="partner-primary-button" disabled={locations.length === 0 || seasons.length === 0} onClick={() => setCreating((current) => !current)}>
            {creating ? 'Cancel' : '+ Create squad'}
          </button>
        </div>
      </div>
      {editError && <div role="alert" className="partner-notice partner-notice--error">{editError}</div>}
      {creating && (
        <form className="partner-squad-inline-create" onSubmit={submitCreate}>
          <label><span>Squad name</span><input autoFocus required maxLength={100} value={createTitle} onChange={(event) => setCreateTitle(event.target.value)} placeholder={`e.g., ${partner.name} Music`} /></label>
          <label><span>Location</span><select required value={newSquadLocationId} onChange={(event) => onLocationChange(event.target.value)}><option value="">Choose location…</option>{locations.map((location) => <option key={location.id} value={location.id}>{location.name}</option>)}</select></label>
          <label><span>Season</span><select required value={createSeasonId} onChange={(event) => setCreateSeasonId(event.target.value)}><option value="">Choose season…</option>{seasons.map((season) => <option key={season.id} value={season.termId || season.id}>{season.label || programTermLabelFromId(season.termId)}</option>)}</select></label>
          <label><span>Schedule</span><select value={createScheduleKind} onChange={(event) => setCreateScheduleKind(event.target.value as 'recurring' | 'single_day')}><option value="recurring">Recurring program</option><option value="single_day">Single-day event</option></select></label>
          {createScheduleKind === 'single_day' && <label><span>Event date</span><input type="date" required value={createMainDate} onChange={(event) => setCreateMainDate(event.target.value)} /></label>}
          {createScheduleKind === 'single_day' && <label><span>Starts</span><input type="time" required value={createStartTime} onChange={(event) => setCreateStartTime(event.target.value)} /></label>}
          {createScheduleKind === 'single_day' && <label><span>Ends</span><input type="time" required value={createEndTime} onChange={(event) => setCreateEndTime(event.target.value)} /></label>}
          {createScheduleKind === 'single_day' && <label><span>Event kind</span><select value={createSingleDayType} onChange={(event) => setCreateSingleDayType(event.target.value as 'class' | 'demo')}><option value="class">Class</option><option value="demo">Demo</option></select></label>}
          {createScheduleKind === 'single_day' && createSingleDayType === 'demo' && <label><span>Demo category</span><select required value={createDemoType || ''} onChange={(event) => setCreateDemoType((event.target.value || undefined) as Squad['demoType'])}><option value="">Choose category…</option><option value="lunchtime">Lunch</option><option value="event_booth">Booth</option><option value="school_assembly">Assembly</option><option value="classroom_visit">Classroom</option><option value="conference">Conference</option><option value="other">Other</option></select></label>}
          <button type="submit" className="partner-primary-button" disabled={createBusy || !createTitle.trim() || !newSquadLocationId || !createSeasonId || (createScheduleKind === 'single_day' && (!createMainDate || !createStartTime || !createEndTime || createEndTime <= createStartTime || (createSingleDayType === 'demo' && !createDemoType)))}>{createBusy ? 'Creating…' : 'Create squad'}</button>
        </form>
      )}
      {visibleSquads.length > 0 && (
        <table className="partner-data-table">
          <thead><tr><th>Squad</th><th>Season</th><th>Location</th><th>Schedule</th><th>Enrollment</th><th>Teacher</th><th>Status</th><th>Actions</th></tr></thead>
          <tbody>
            {visibleSquads.map((squad) => {
              const linkedLocation = locations.find((location) => location.id === squad.locationId);
              const enrolled = squad.studentIds?.length || squad.seatsTaken || 0;
              const capacity = squad.capacity;
              const localSchedule = formatSquadSchedule(squad, partner.timezone);
              const isSingleDay = squad.scheduleKind === 'single_day' || Boolean(squad.schedule?.mainDate);
              const scheduleMainDate = formatScheduleDate(squad.schedule?.mainDate);
              const scheduleStartDate = formatScheduleDate(squad.schedule?.startDate);
              const scheduleEndDate = formatScheduleDate(squad.schedule?.endDate);
              const totalClasses = Number.isFinite(Number(squad.totalClasses)) && Number(squad.totalClasses) > 0
                ? Number(squad.totalClasses)
                : computeTotalClassesFromSchedule(squad.schedule);
              const noClassDates = Array.from(new Set([
                ...(squad.schedule?.exceptions || []),
                ...(squad.schedule?.inheritedExceptions || []),
              ])).sort().map(formatScheduleDate).filter(Boolean);
              const inferredSeason = inferSeasonFromStartDate(effectiveScheduleDate(squad), seasons);
              const assignedTermId = squad.termId || squad.seasonId || '';
              const displayedTermId = inferredSeason?.termId || assignedTermId;
              const seasonConflict = Boolean(inferredSeason && assignedTermId && inferredSeason.termId !== assignedTermId);
              const isCanceled = ['canceled', 'cancelled'].includes(String(squad.status || '').toLowerCase());
              return (
                <tr key={squad.id} className={isCanceled ? 'partner-squad-row--canceled' : undefined}>
                  <td>
                    {editingTitleSquadId === squad.id ? (
                      <input
                        className="partner-inline-edit"
                        value={editingTitle}
                        onChange={(event) => setEditingTitle(event.target.value)}
                        onBlur={() => void saveTitle(squad)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') void saveTitle(squad);
                          if (event.key === 'Escape') setEditingTitleSquadId(null);
                        }}
                        disabled={savingCell === `${squad.id}:title`}
                        autoFocus
                      />
                    ) : (
                      <button
                        type="button"
                        className="partner-table-link"
                        title="Click to rename squad"
                        onClick={() => {
                          setEditingTitleSquadId(squad.id);
                          setEditingTitle(squad.title || '');
                        }}
                      >
                        {squad.title || 'Untitled squad'}
                      </button>
                    )}
                  </td>
                  <td>
                    <select className="partner-inline-select" aria-label={`Season for ${squad.title}`} value={assignedTermId} disabled={savingCell === `${squad.id}:season`} onChange={(event) => void saveSeasonAssignment(squad, event.target.value)}>
                      <option value="">Choose season…</option>
                      {seasons.map((season) => <option key={season.id} value={season.termId}>{season.label || programTermLabelFromId(season.termId)}</option>)}
                    </select>
                    {seasonConflict ? <button type="button" className="partner-season-suggestion" disabled={savingCell === `${squad.id}:season`} onClick={() => void saveSeasonAssignment(squad, inferredSeason!.termId)}>Use {programTermLabelFromId(inferredSeason!.termId)} · start date match</button> : <div className={`partner-help-text ${assignedTermId && !inferredSeason ? 'partner-help-text--season-source' : ''}`}>{inferredSeason && assignedTermId ? 'Assignment matches first class date' : inferredSeason ? `${programTermLabelFromId(displayedTermId)} suggested by first class date` : assignedTermId ? 'Assigned from creation context' : 'Set the first class date to suggest a season'}</div>}
                  </td>
                  <td>
                    <select
                      className="partner-inline-select"
                      aria-label={`Location for ${squad.title}`}
                      value={squad.locationId || ''}
                      disabled={savingCell === `${squad.id}:location`}
                      onChange={(event) => {
                        const location = locations.find(item => item.id === event.target.value);
                        void saveSquadPatch(squad.id, 'location', {
                          locationId: event.target.value,
                          locationName: location?.name || '',
                        });
                      }}
                    >
                      <option value="">Not assigned</option>
                      {locations.map(location => <option value={location.id} key={location.id}>{location.name}</option>)}
                    </select>
                    {!squad.locationId && <div className="partner-help-text">{linkedLocation?.name || squad.locationName || squad.schoolName || ''}</div>}
                  </td>
                  <td>
                    <button type="button" className="partner-table-link" title="Open squad details to edit schedule" onClick={() => onOpenSquad(squad.id)}>
                      <span>{localSchedule.schedule}</span>
                      <span className="partner-schedule-dates">
                        {isSingleDay && scheduleMainDate
                          ? scheduleMainDate
                          : scheduleStartDate && scheduleEndDate
                          ? `${scheduleStartDate} – ${scheduleEndDate}`
                          : scheduleStartDate
                            ? `Starts ${scheduleStartDate} · end date needed`
                            : scheduleEndDate
                              ? `Start date needed · ends ${scheduleEndDate}`
                              : 'Start and end dates needed'}
                        {totalClasses !== null && totalClasses !== undefined
                          ? ` · ${totalClasses} ${totalClasses === 1 ? 'class' : 'classes'}`
                          : ''}
                      </span>
                      {noClassDates.length > 0 && (
                        <span className="partner-schedule-no-class">No class: {noClassDates.join(', ')}</span>
                      )}
                    </button>
                  </td>
                  <td>
                    <div className="partner-enrollment-edit">
                      <span>{enrolled} /</span>
                      <input
                        key={`${squad.id}:${capacity || ''}`}
                        type="number"
                        min={enrolled}
                        defaultValue={capacity || ''}
                        aria-label={`Capacity for ${squad.title}`}
                        disabled={savingCell === `${squad.id}:capacity`}
                        onBlur={(event) => {
                          const nextCapacity = Number(event.target.value);
                          if (Number.isFinite(nextCapacity) && nextCapacity >= enrolled && nextCapacity !== capacity) {
                            void saveSquadPatch(squad.id, 'capacity', { capacity: nextCapacity });
                          }
                        }}
                      />
                    </div>
                  </td>
                  <td><button type="button" className="partner-table-link" onClick={() => onOpenSquad(squad.id)}>{squad.leadTeacherId ? 'Lead assigned' : 'Lead needed'}<div className="partner-help-text">{squad.teacherIds?.length || 0} with access</div></button></td>
                  <td>
                    {isCanceled ? (
                      <button type="button" className="partner-table-link" title="Open squad details to reactivate a canceled squad" onClick={() => onOpenSquad(squad.id)}>{humanize(squad.status || 'Canceled')}</button>
                    ) : (
                      <select
                        className="partner-inline-select"
                        aria-label={`Status for ${squad.title}`}
                        value={squad.status || 'published'}
                        disabled={savingCell === `${squad.id}:status`}
                        onChange={(event) => {
                          const status = event.target.value as Squad['status'];
                          if (status === 'archived' && shouldConfirmArchiveInsteadOfCancel({ ...squad, timezone: squad.timezone || partner.timezone })) {
                            setArchiveConfirmSquad(squad);
                            return;
                          }
                          void saveSquadStatus(squad.id, status);
                        }}
                      >
                        <option value="lead">Lead</option>
                        <option value="published">Published</option>
                        <option value="draft">Draft</option>
                        <option value="archived">Archived</option>
                        <option value="canceled">Canceled</option>
                      </select>
                    )}
                  </td>
                  <td>
                    <div className="partner-squad-menu">
                      <button type="button" className="partner-squad-menu__trigger" onClick={() => setOpenMenuSquadId((current) => current === squad.id ? null : squad.id)} aria-label={`Actions for ${squad.title || 'squad'}`} aria-haspopup="menu" aria-expanded={openMenuSquadId === squad.id}>•••</button>
                      {openMenuSquadId === squad.id && <div className="partner-squad-menu__popover" role="menu">
                        <button type="button" role="menuitem" onClick={() => { setOpenMenuSquadId(null); onLaunchDashboard(squad.id); }}>Open dashboard</button>
                        <button type="button" role="menuitem" onClick={() => { setOpenMenuSquadId(null); onOpenSquad(squad.id); }}>Details</button>
                        <button type="button" role="menuitem" onClick={() => { setOpenMenuSquadId(null); onManageUsers(squad); }}>Users</button>
                        <button type="button" role="menuitem" className="danger" onClick={() => { setOpenMenuSquadId(null); onDelete(squad); }}>Delete</button>
                      </div>}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {visibleSquads.length === 0 && (
        <div className="partner-empty-state">
          <strong>{squads.length === 0 ? 'No squads linked' : 'No squads match this filter'}</strong>
          <span>{squads.length === 0 ? 'Choose a location above to create the first squad.' : 'Change the status filter to see other programs.'}</span>
        </div>
      )}
      {archiveConfirmSquad && (() => {
        const timezone = archiveConfirmSquad.timezone || partner.timezone || DEFAULT_TZ;
        const startDate = effectiveScheduleDate(archiveConfirmSquad, 'start');
        const today = formatTimeInZone(new Date(), timezone, 'yyyy-MM-dd');
        const hasStarted = Boolean(startDate) && startDate <= today;
        return (
          <ConfirmationModal
            isOpen
            title={`Archive ${archiveConfirmSquad.title || 'this squad'}?`}
            message={`${hasStarted ? 'This squad is still in progress.' : "This squad hasn't started yet."} Archiving hides it without recording that the class was called off. If this class isn't happening, cancel it instead.`}
            confirmText="Cancel squad"
            secondaryText="Archive anyway"
            cancelText="Go back"
            onConfirm={() => {
              const squadId = archiveConfirmSquad.id;
              setArchiveConfirmSquad(null);
              void saveSquadStatus(squadId, 'canceled');
            }}
            onSecondary={() => {
              const squadId = archiveConfirmSquad.id;
              setArchiveConfirmSquad(null);
              void saveSquadStatus(squadId, 'archived');
            }}
            onClose={() => setArchiveConfirmSquad(null)}
          />
        );
      })()}
    </section>
  );
}

function ParentsSection(props: {
  partner: Partner;
  parentDirectory: PartnerParentDirectory | null;
  parentDirectoryLoading: boolean;
  legacyParentRows: string[];
  linkedStudentCount: number;
}): React.ReactElement {
  const { partner, parentDirectory, parentDirectoryLoading, legacyParentRows, linkedStudentCount } = props;
  const [search, setSearch] = useState('');
  const needle = search.trim().toLowerCase();
  const filteredParents = (parentDirectory?.parents || []).filter((parentRow) => {
    const studentNames = parentRow.studentIds.map((studentId) => parentDirectory?.students.find((student) => student.id === studentId)?.displayName || studentId);
    return !needle || [parentRow.displayName, parentRow.email, ...parentRow.phones, ...studentNames]
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(needle));
  });
  const policy = partner.parentCommunicationPolicy || {};
  const outreachAllowed = policy.directOutreachAllowed === true;
  const pickupLabel = (preference: { options: string[]; otherText?: string } | null): string => {
    if (!preference || !Array.isArray(preference.options) || preference.options.length === 0) return '—';
    const labels: Record<string, string> = {
      aftercare: 'Aftercare', guardian_pickup: 'Guardian pickup', bike_walk: 'Bike/walk', other: 'Other',
    };
    const value = preference.options.map(option => labels[option] || option).join(', ');
    return preference.options.includes('other') && preference.otherText ? `${value}: ${preference.otherText}` : value;
  };

  return (
    <section>
      <div className="partner-section-header">
        <div>
          <h2>Parents</h2>
          <p>Resolved from linked squads, students, parents, and enrollments—never duplicated into the partner record.</p>
        </div>
      </div>
      <div className={`partner-notice ${outreachAllowed ? 'partner-notice--success' : 'partner-notice--error'}`}>
        <span>
          <strong>{outreachAllowed ? 'Direct outreach allowed.' : 'Direct outreach is not approved.'}</strong>{' '}
          Parent accounts {policy.parentAccountsAllowed ? 'are' : 'are not'} allowed and parent emails {policy.parentEmailsAvailable ? 'are' : 'are not'} available.
          {policy.notes ? ` ${policy.notes}` : ''}
        </span>
      </div>
      <div className="partner-summary-grid">
        <div className="partner-summary-tile"><strong>{parentDirectory?.students.length ?? linkedStudentCount}</strong><span>Linked students</span></div>
        <div className="partner-summary-tile"><strong>{parentDirectory?.parents.length ?? legacyParentRows.length}</strong><span>Resolved parents</span></div>
      </div>
      <input aria-label="Search parents" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search parents, email, phone, or student…" />
      {parentDirectoryLoading && <div className="partner-loading">Resolving parent directory…</div>}
      {parentDirectory && filteredParents.length > 0 && (
        <table className="partner-data-table" style={{ marginTop: 16 }}>
          <thead><tr><th>Parent</th><th>Email</th><th>Phone</th><th>Student safety details</th></tr></thead>
          <tbody>
            {filteredParents.map((parentRow) => (
              <tr key={parentRow.id}>
                <td><strong>{parentRow.displayName || parentRow.id}</strong><PhoneCallButton partnerId={partner.id} person={{ type: 'parent', id: parentRow.id, displayName: parentRow.displayName || parentRow.email || parentRow.id }} /></td>
                <td>{parentRow.email ? <a href={`mailto:${parentRow.email}`}>{parentRow.email}</a> : '—'}</td>
                <td>{parentRow.phones.length ? parentRow.phones.join(', ') : '—'}</td>
                <td>{parentRow.studentIds.length ? parentRow.studentIds.map((studentId) => {
                  const student = parentDirectory.students.find((item) => item.id === studentId);
                  return (
                    <div key={studentId} style={{ marginBottom: 8 }}>
                      <strong>{student?.displayName || studentId}</strong><br />
                      <span>Pickup: {pickupLabel(student?.pickupPreference || null)}</span><br />
                      <span>Photos: {student?.photoConsent === 'accept' ? '✅ Allowed' : student?.photoConsent === 'deny' ? '❌ Not allowed' : '— No response'}</span>
                    </div>
                  );
                }) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {parentDirectory && filteredParents.length === 0 && <div className="partner-empty-state"><strong>{needle ? 'No parents match this search' : 'No resolved parents'}</strong><span>{needle ? 'Try a different name, email, phone, or student.' : 'Link students and parent accounts through squads to populate this directory.'}</span></div>}
      {legacyParentRows.length > 0 && (
        <div className="partner-subsection">
          <h3>Legacy records to reconcile</h3>
          <p className="partner-section-intro">These raw entries came from squad records and may duplicate resolved parent accounts.</p>
          <ul>{legacyParentRows.slice(0, 30).map((row) => <li key={row}>{row}</li>)}</ul>
        </div>
      )}
    </section>
  );
}

function CopyablePublicUrl(props: {
  url: string;
  label?: string;
  badge?: string | null;
}): React.ReactElement {
  const { url, label, badge } = props;
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      setCopied(false);
    }
  };
  return (
    <div className="partner-public-url">
      {(label || badge) ? (
        <div className="partner-public-url__meta">
          {label ? <span className="partner-public-url__label">{label}</span> : null}
          {badge ? <span className="partner-pill partner-pill--warning">{badge}</span> : null}
        </div>
      ) : null}
      <code>{url}</code>
      <div className="partner-actions partner-actions--compact">
        <button type="button" onClick={() => void copy()}>{copied ? 'Copied' : 'Copy'}</button>
        <a href={url} target="_blank" rel="noreferrer">Open</a>
      </div>
    </div>
  );
}

function OverviewSection(props: {
  partner: Partner;
  locations: PartnerLocation[];
  inferredMetro: string;
  saving: boolean;
  deleting: boolean;
  actorId: string | null;
  onSave: (patch: Partial<Partner>) => Promise<boolean>;
  onTogglePublic: (isPublic: boolean) => Promise<void> | void;
  onDeletePartner: () => void;
  onLocationsChanged: () => Promise<void>;
}): React.ReactElement {
  const {
    partner,
    locations,
    inferredMetro: metroFallback,
    saving,
    deleting,
    actorId,
    onSave,
    onTogglePublic,
    onDeletePartner,
    onLocationsChanged,
  } = props;
  const [draft, setDraft] = useState({ ...partner, metro: partner.metro || metroFallback });
  const [locationLinks, setLocationLinks] = useState<Record<string, string>>({});
  const [savingLinks, setSavingLinks] = useState(false);
  const [expandedLocationId, setExpandedLocationId] = useState<string | null>(null);
  const [actionsMenuOpen, setActionsMenuOpen] = useState(false);
  useEffect(() => setDraft({ ...partner, metro: partner.metro || metroFallback }), [partner, metroFallback]);
  useEffect(() => {
    const partnerLink = String(partner.registrationLink || '').trim();
    setLocationLinks(Object.fromEntries(
      locations.map((location) => {
        const stored = String(location.registrationLink || '').trim();
        return [location.id, stored && stored !== partnerLink ? stored : ''];
      })
    ));
  }, [locations, partner.registrationLink]);
  useEffect(() => {
    if (!actionsMenuOpen) return;
    const close = () => setActionsMenuOpen(false);
    const timer = window.setTimeout(() => window.addEventListener('click', close), 0);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('click', close);
    };
  }, [actionsMenuOpen]);
  const sortedLocations = locations.slice().sort((left, right) => left.name.localeCompare(right.name));
  const marketingUrl = publicPartnerPageUrl(partner.slug);
  const nonStripe = isNonStripePaymentMode(partner.billingDefaults?.paymentMode);
  const busy = saving || savingLinks;
  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        void (async () => {
          const nextPartnerLink = String(draft.registrationLink || '').trim() || null;
          const saved = await onSave({
            name: draft.name,
            slug: String(partner.slug || '').trim() || slugify(draft.name),
            organizationType: draft.organizationType,
            metro: draft.metro,
            demographics: draft.demographics,
            parentCommunicationPolicy: draft.parentCommunicationPolicy,
            insuranceRequirements: draft.insuranceRequirements,
            vendorProcess: draft.vendorProcess,
            active: draft.active,
            registrationLink: nextPartnerLink,
          });
          if (!saved) return;
          setSavingLinks(true);
          try {
            const locationWrites = locations.filter((location) => {
              const override = String(locationLinks[location.id] || '').trim();
              const next = override || nextPartnerLink || '';
              return next !== String(location.registrationLink || '').trim();
            });
            if (locationWrites.length > 0) {
              await Promise.all(locationWrites.map((location) => {
                const override = String(locationLinks[location.id] || '').trim();
                return updatePartnerLocation(location.id, {
                  registrationLink: override || nextPartnerLink,
                }, actorId);
              }));
              await onLocationsChanged();
            }
          } finally {
            setSavingLinks(false);
          }
        })();
      }}
    >
      <div className="partner-section-header">
        <div>
          <h2>Overview</h2>
          <p>Partner identity, communication permissions, and operating requirements.</p>
        </div>
      </div>

      <div className={`partner-public-banner ${partner.isPublic === true ? 'partner-public-banner--on' : 'partner-public-banner--off'}`}>
        <label className="partner-public-banner__toggle">
          <input
            type="checkbox"
            checked={partner.isPublic === true}
            disabled={saving || deleting}
            onChange={(event) => void onTogglePublic(event.target.checked)}
          />
          <span>
            <strong>{partner.isPublic === true ? 'Visible publicly' : 'Not visible publicly'}</strong>
            <small>Locations follow this setting.</small>
          </span>
        </label>
        <div className="partner-overflow-menu">
          <button
            type="button"
            className="partner-overflow-menu__trigger"
            aria-label={`More actions for ${partner.name}`}
            aria-haspopup="menu"
            aria-expanded={actionsMenuOpen}
            disabled={deleting}
            onClick={(event) => {
              event.stopPropagation();
              setActionsMenuOpen((open) => !open);
            }}
          >
            •••
          </button>
          {actionsMenuOpen && (
            <div
              className="partner-overflow-menu__popover"
              role="menu"
              onClick={(event) => event.stopPropagation()}
            >
              <button
                type="button"
                role="menuitem"
                className="danger"
                disabled={deleting}
                onClick={() => {
                  setActionsMenuOpen(false);
                  onDeletePartner();
                }}
              >
                {deleting ? 'Deleting…' : 'Delete partner'}
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="partner-field-grid">
        <label className="partner-field">Partner name<input value={draft.name} onChange={(event) => setDraft({ ...draft, name: event.target.value })} /></label>
        <label className="partner-field">Metro<input value={draft.metro || ''} onChange={(event) => setDraft({ ...draft, metro: event.target.value })} placeholder="e.g., Chicago" /></label>
        <label className="partner-field">
          Organization type
          <select value={draft.organizationType || ''} onChange={(event) => setDraft({ ...draft, organizationType: event.target.value })}>
            <option value="">Unclassified</option>
            {Object.entries(organizationTypeLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </label>
        <label className="partner-field">Average household income<input type="number" value={draft.demographics?.avgHouseholdIncomeUsd ?? ''} onChange={(event) => setDraft({ ...draft, demographics: { ...draft.demographics, avgHouseholdIncomeUsd: event.target.value ? Number(event.target.value) : null } })} /></label>
        <label className="partner-field">Reliability score (1–5)<input type="number" min={1} max={5} value={draft.demographics?.reliabilityScore ?? ''} onChange={(event) => setDraft({ ...draft, demographics: { ...draft.demographics, reliabilityScore: event.target.value ? Number(event.target.value) : null } })} /></label>
        <label className="partner-field">Demographic notes<textarea value={draft.demographics?.notes || ''} onChange={(event) => setDraft({ ...draft, demographics: { ...draft.demographics, notes: event.target.value } })} /></label>
      </div>

      <div className="partner-subsection">
        <h3>Public registration</h3>
        <p className="partner-section-intro">
          {nonStripe
            ? 'Share the Overture page in flyers and emails. Families land there and are sent to the partner’s registration system (MyRec, RecDesk, or similar).'
            : 'Share the Overture page in flyers and emails. Add an external registration URL if any programs are paid through the partner instead of Stripe.'}
        </p>
        {marketingUrl ? (
          <CopyablePublicUrl
            url={marketingUrl}
            label="Overture partner page"
            badge={draft.isPublic === true && draft.active !== false ? null : 'Not on website'}
          />
        ) : (
          <p className="partner-help-text">A public URL will appear once this partner has a slug.</p>
        )}
        <label className="partner-field" style={{ marginTop: 14 }}>
          External registration URL
          <small>Default for every location. Parents see this as “Register with [partner name]” on the public signup page when the program is not paid through Stripe.</small>
          <input
            type="url"
            placeholder="https://www.myrec.com/..."
            value={draft.registrationLink || ''}
            onChange={(event) => setDraft({ ...draft, registrationLink: event.target.value })}
          />
        </label>
        {sortedLocations.length > 0 && (
          <div className="partner-location-accordion-list">
            {sortedLocations.map((location) => {
              const expanded = expandedLocationId === location.id;
              const pageUrl = publicLocationPageUrl(location.slug);
              const effectiveUrl = effectiveRegistrationLink(
                location.id,
                locationLinks[location.id],
                null,
                draft.registrationLink
              );
              return (
                <article
                  key={location.id}
                  className={`partner-location-accordion${expanded ? ' partner-location-accordion--open' : ''}`}
                >
                  <button
                    type="button"
                    className="partner-location-accordion__trigger"
                    aria-expanded={expanded}
                    onClick={() => setExpandedLocationId(expanded ? null : location.id)}
                  >
                    <span className="partner-location-accordion__identity">
                      <strong>{location.name}</strong>
                      {location.active === false && <span className="partner-pill partner-pill--muted">Inactive</span>}
                    </span>
                    <span className="partner-location-accordion__chevron" aria-hidden="true">{expanded ? '−' : '+'}</span>
                  </button>
                  {expanded && (
                    <div className="partner-location-accordion__body">
                      {pageUrl ? <CopyablePublicUrl url={pageUrl} label="Overture page" /> : (
                        <p className="partner-help-text">A public school URL will appear once this location has a slug.</p>
                      )}
                      <label className="partner-field">
                        Location override
                        <small>Leave blank to use the partner URL above.</small>
                        <input
                          type="url"
                          placeholder={String(draft.registrationLink || '').trim() || 'https://www.myrec.com/...'}
                          value={locationLinks[location.id] || ''}
                          onChange={(event) => setLocationLinks((current) => ({
                            ...current,
                            [location.id]: event.target.value,
                          }))}
                        />
                      </label>
                      {effectiveUrl ? (
                        <CopyablePublicUrl url={effectiveUrl} label="Parents are sent here" />
                      ) : nonStripe ? (
                        <p className="partner-help-text partner-help-text--warning">Add a partner or location URL so the public signup page can send families to the school portal.</p>
                      ) : null}
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        )}
      </div>

      <div className="partner-subsection">
        <h3>Parent communication policy</h3>
        <div className="partner-checkbox-grid">
          {[
            ['directOutreachAllowed', 'Overture may contact parents directly'],
            ['parentAccountsAllowed', 'Parent accounts allowed'],
            ['parentEmailsAvailable', 'Parent emails available'],
            ['partnerCanSendAccountEmail', 'Partner can send account email'],
          ].map(([key, label]) => (
            <label key={key} className="partner-checkbox">
              <input
                type="checkbox"
                checked={Boolean((draft.parentCommunicationPolicy as any)?.[key])}
                onChange={(event) => setDraft({
                  ...draft,
                  parentCommunicationPolicy: { ...draft.parentCommunicationPolicy, [key]: event.target.checked },
                })}
              /> {label}
            </label>
          ))}
        </div>
        <label className="partner-field" style={{ marginTop: 14 }}>Policy notes<textarea value={draft.parentCommunicationPolicy?.notes || ''} onChange={(event) => setDraft({ ...draft, parentCommunicationPolicy: { ...draft.parentCommunicationPolicy, notes: event.target.value } })} /></label>
      </div>

      <div className="partner-subsection">
        <h3>Insurance and vendor process</h3>
        <div className="partner-checkbox-grid" style={{ marginBottom: 14 }}>
          <label className="partner-checkbox"><input type="checkbox" checked={Boolean(draft.insuranceRequirements?.coiRequired)} onChange={(event) => setDraft({ ...draft, insuranceRequirements: { ...draft.insuranceRequirements, coiRequired: event.target.checked } })} /> COI required</label>
          <label className="partner-checkbox"><input type="checkbox" checked={Boolean(draft.insuranceRequirements?.additionalInsuredRequired)} onChange={(event) => setDraft({ ...draft, insuranceRequirements: { ...draft.insuranceRequirements, additionalInsuredRequired: event.target.checked } })} /> Additional insured required</label>
          <label className="partner-checkbox"><input type="checkbox" checked={Boolean(draft.vendorProcess?.required)} onChange={(event) => setDraft({ ...draft, vendorProcess: { ...draft.vendorProcess, required: event.target.checked } })} /> Vendor process required</label>
        </div>
        <div className="partner-field-grid">
          <label className="partner-field">General liability coverage (USD)<input type="number" value={draft.insuranceRequirements?.generalLiabilityAmountUsd ?? ''} onChange={(event) => setDraft({ ...draft, insuranceRequirements: { ...draft.insuranceRequirements, generalLiabilityAmountUsd: event.target.value ? Number(event.target.value) : null } })} /></label>
          <label className="partner-field">Registration software<input value={draft.vendorProcess?.registrationSoftware || ''} onChange={(event) => setDraft({ ...draft, vendorProcess: { ...draft.vendorProcess, registrationSoftware: event.target.value } })} /></label>
          <label className="partner-field">Vendor portal URL<input value={draft.vendorProcess?.portalUrl || ''} onChange={(event) => setDraft({ ...draft, vendorProcess: { ...draft.vendorProcess, portalUrl: event.target.value } })} /></label>
          <label className="partner-field">COI wording and notes<textarea value={draft.insuranceRequirements?.wording || ''} onChange={(event) => setDraft({ ...draft, insuranceRequirements: { ...draft.insuranceRequirements, wording: event.target.value } })} /></label>
          <label className="partner-field">Vendor instructions<textarea value={draft.vendorProcess?.instructions || ''} onChange={(event) => setDraft({ ...draft, vendorProcess: { ...draft.vendorProcess, instructions: event.target.value } })} /></label>
        </div>
      </div>

      <div className="partner-subsection">
        <h3>Availability</h3>
        <p className="partner-section-intro">This setting affects whether the partner can be used internally. Public website visibility is controlled at the top of this page.</p>
        <div className="partner-checkbox-grid">
          <label className="partner-checkbox"><input type="checkbox" checked={draft.active !== false} onChange={(event) => setDraft({ ...draft, active: event.target.checked })} /> Active for internal operations</label>
        </div>
      </div>

      <div className="partner-actions">
        <button type="submit" disabled={busy}>{busy ? 'Saving overview…' : 'Save overview'}</button>
        <button type="button" onClick={() => {
          setDraft({ ...partner, metro: partner.metro || metroFallback });
          const partnerLink = String(partner.registrationLink || '').trim();
          setLocationLinks(Object.fromEntries(
            locations.map((location) => {
              const stored = String(location.registrationLink || '').trim();
              return [location.id, stored && stored !== partnerLink ? stored : ''];
            })
          ));
        }}>Reset changes</button>
      </div>
    </form>
  );
}

function LocationsSection(props: {
  partner: Partner;
  locations: PartnerLocation[];
  actorId: string | null;
  onChanged: () => Promise<void>;
}): React.ReactElement {
  const { partner, locations, actorId, onChanged } = props;
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [saving, setSaving] = useState(false);
  return (
    <section>
      <div className="partner-section-header">
        <div>
          <h2>Locations</h2>
          <p>On-site access, technology, safety instructions, and registration details. Locations appear on the public website when the partner is visible publicly.</p>
        </div>
      </div>
      <form
        onSubmit={async (event) => {
          event.preventDefault();
          setSaving(true);
          try {
            await createPartnerLocation({
              partnerId: partner.id,
              name: name.trim(),
              slug: slugify(name),
              address: address.trim(),
              ...(await geocodeLocationAddress(address.trim()) || {}),
              active: true,
              isPublic: true,
              campusPhotoUrls: [],
              siteContactIds: [],
              createdBy: actorId,
              updatedBy: actorId,
            }, actorId);
            setName('');
            setAddress('');
            await onChanged();
          } finally {
            setSaving(false);
          }
        }}
        className="partner-inline-create"
      >
        <input aria-label="New location name" value={name} onChange={(event) => setName(event.target.value)} placeholder="Location name" required />
        <input aria-label="New location address" value={address} onChange={(event) => setAddress(event.target.value)} placeholder="Street, city, state, ZIP" required />
        <button type="submit" disabled={saving}>{saving ? 'Adding…' : 'Add location'}</button>
      </form>
      <div className="partner-record-list">
        {locations.map((location) => (
          <LocationCard
            key={location.id}
            location={location}
            partnerRegistrationLink={partner.registrationLink}
            actorId={actorId}
            onChanged={onChanged}
          />
        ))}
      </div>
      {locations.length === 0 && <div className="partner-empty-state"><strong>No locations yet</strong><span>Add the first operating site to unlock squad creation.</span></div>}
    </section>
  );
}

function LocationCard(props: {
  location: PartnerLocation;
  partnerRegistrationLink?: string | null;
  actorId: string | null;
  onChanged: () => Promise<void>;
}): React.ReactElement {
  const { location, partnerRegistrationLink, actorId, onChanged } = props;
  const [draft, setDraft] = useState(location);
  const [saving, setSaving] = useState(false);
  const [visibilitySaving, setVisibilitySaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [photoFailed, setPhotoFailed] = useState(false);
  useEffect(() => setDraft(location), [location]);
  const campusPhotoUrl = draft.campusPhotoUrls?.[0];
  useEffect(() => setPhotoFailed(false), [campusPhotoUrl]);
  const setHiddenFromWebsite = async (hidden: boolean) => {
    setVisibilitySaving(true);
    setDraft((current) => ({ ...current, isPublic: !hidden }));
    try {
      await updatePartnerLocation(location.id, { isPublic: !hidden }, actorId);
      await onChanged();
    } catch (error) {
      setDraft((current) => ({ ...current, isPublic: location.isPublic }));
      console.error('Failed to update location public visibility', error);
    } finally {
      setVisibilitySaving(false);
    }
  };
  const logisticsChecks = [
    Boolean(location.emergencyContactOnSite),
    Boolean(location.parkingInstructions),
    Boolean(location.arrivalProcedure),
    Boolean(location.dismissalProcedure),
    Boolean(location.lateArrivalProcedure),
    Boolean(location.hallwayRules),
    Boolean(location.bathroomRules),
    Boolean(location.wifiUsername || location.wifiPassword || location.wifiRules),
    location.techTestedOnSite === true,
    location.sitesWhitelisted === true,
  ];
  const logisticsComplete = logisticsChecks.filter(Boolean).length;
  const uploadCampusPhoto = async (file: File, input: HTMLInputElement) => {
    setUploading(true);
    try {
      const url = await uploadLocationCampusPhoto({ locationId: location.id, file, actorId });
      setDraft((current) => ({ ...current, campusPhotoUrls: [url] }));
      setPhotoFailed(false);
      await onChanged();
    } finally {
      setUploading(false);
      input.value = '';
    }
  };
  const clearCampusPhoto = async () => {
    setUploading(true);
    try {
      await removeLocationCampusPhoto({ locationId: location.id, actorId });
      setDraft((current) => ({ ...current, campusPhotoUrls: [] }));
      setPhotoFailed(false);
      await onChanged();
    } finally {
      setUploading(false);
    }
  };
  const photoUploader = (
    <div className="partner-location-photo-stack">
      <label className={`partner-location-photo-upload${uploading ? ' partner-location-photo-upload--busy' : ''}`} title={`Upload a campus photo for ${location.name}`}>
        <input
          type="file"
          accept="image/*"
          disabled={uploading}
          aria-label={`Upload a campus photo for ${location.name}`}
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) void uploadCampusPhoto(file, event.target);
          }}
        />
        <span className="partner-location-photo">
          {campusPhotoUrl && !photoFailed ? (
            <img src={campusPhotoUrl} alt="" onError={() => setPhotoFailed(true)} />
          ) : (
            <span>{location.name.slice(0, 1).toUpperCase()}</span>
          )}
          <span className="partner-location-photo__action" aria-hidden="true">{uploading ? 'Uploading…' : 'Change'}</span>
        </span>
      </label>
      {campusPhotoUrl && expanded ? (
        <button type="button" className="partner-location-photo-remove" disabled={uploading} onClick={() => void clearCampusPhoto()}>
          Remove photo
        </button>
      ) : null}
    </div>
  );

  if (!expanded) {
    return (
      <article className="partner-record-card partner-location-card">
        <div className="partner-location-card__header">
          <div className="partner-location-card__identity">
            {photoUploader}
            <div>
              <h3>{location.name}</h3>
              <div className="partner-help-text">{location.address || 'Address not set'}</div>
            </div>
          </div>
          <div className="partner-actions partner-actions--compact">
            <label className="partner-location-public-toggle" onClick={(event) => event.stopPropagation()}>
              <input
                type="checkbox"
                checked={draft.isPublic === false}
                disabled={visibilitySaving}
                onChange={(event) => void setHiddenFromWebsite(event.target.checked)}
              />
              <span>{visibilitySaving ? 'Saving…' : 'Hide on public website'}</span>
            </label>
            <span className={`partner-pill ${location.active === false ? 'partner-pill--muted' : 'partner-pill--success'}`}>{location.active === false ? 'Inactive' : 'Active'}</span>
            <button type="button" onClick={() => setExpanded(true)}>Manage location</button>
          </div>
        </div>
        <div className="partner-location-summary">
          <div className="partner-location-readiness">
            <div><strong>Logistics</strong><span>{logisticsComplete}/{logisticsChecks.length} complete</span></div>
            <div className="partner-progress" aria-label={`${logisticsComplete} of ${logisticsChecks.length} logistics items complete`}>
              <span style={{ width: `${Math.round((logisticsComplete / logisticsChecks.length) * 100)}%` }} />
            </div>
          </div>
          <div>
            <strong>Registration</strong>
            <div className="partner-help-text">
              {effectiveRegistrationLink(location.id, location.registrationLink, null, partnerRegistrationLink)
                ? (location.registrationLink ? 'Location override set' : 'Using partner URL')
                : 'Link needed'}
            </div>
          </div>
          <div>
            <strong>Technology</strong>
            <div className="partner-help-text">{location.techTestedOnSite && location.sitesWhitelisted ? 'Ready on site' : 'Setup incomplete'}</div>
          </div>
        </div>
      </article>
    );
  }
  return (
    <form
      className="partner-record-card partner-location-form"
      onSubmit={async (event) => {
        event.preventDefault();
        setSaving(true);
        try {
          await updatePartnerLocation(location.id, {
            name: draft.name,
            slug: slugify(draft.name),
            address: draft.address,
            ...(await geocodeLocationAddress(String(draft.address || '')) || {}),
            registrationLink: String(draft.registrationLink || '').trim() || String(partnerRegistrationLink || '').trim() || null,
            wifiUsername: draft.wifiUsername,
            wifiPassword: draft.wifiPassword,
            wifiRules: draft.wifiRules,
            parkingInstructions: draft.parkingInstructions,
            arrivalProcedure: draft.arrivalProcedure,
            dismissalProcedure: draft.dismissalProcedure,
            lateArrivalProcedure: draft.lateArrivalProcedure,
            hallwayRules: draft.hallwayRules,
            bathroomRules: draft.bathroomRules,
            emergencyContactOnSite: draft.emergencyContactOnSite,
            hasProjectorOrTv: draft.hasProjectorOrTv,
            projectorConnectionInstructions: draft.projectorConnectionInstructions,
            techTestedOnSite: draft.techTestedOnSite,
            sitesWhitelisted: draft.sitesWhitelisted,
            active: draft.active,
          }, actorId);
          await onChanged();
          setExpanded(false);
        } finally {
          setSaving(false);
        }
      }}
    >
      <div className="partner-location-card__header">
        <div className="partner-location-card__identity">
          {photoUploader}
          <div>
            <h3>{location.name}</h3>
            <div className="partner-help-text">{draft.address || 'Address not set'}</div>
            <span className={`partner-pill ${draft.active === false ? 'partner-pill--muted' : 'partner-pill--success'}`}>{draft.active === false ? 'Inactive' : 'Active'}</span>
          </div>
        </div>
        <div className="partner-actions partner-actions--compact">
          {draft.address && <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(draft.address)}`} target="_blank" rel="noreferrer">Open map</a>}
          {(draft.registrationLink || partnerRegistrationLink) && (
            <a href={String(draft.registrationLink || partnerRegistrationLink)} target="_blank" rel="noreferrer">Registration page</a>
          )}
        </div>
      </div>

      <div className="partner-field-grid">
        <label className="partner-field">Location name<input value={draft.name} onChange={(event) => setDraft({ ...draft, name: event.target.value })} /></label>
        <label className="partner-field">Address<input value={draft.address || ''} onChange={(event) => setDraft({ ...draft, address: event.target.value })} /></label>
        <label className="partner-field">
          External registration link
          <small>Overrides the partner URL from Overview. Leave blank to use the partner default (MyRec, RecDesk, etc.).</small>
          <input type="url" value={draft.registrationLink || ''} onChange={(event) => setDraft({ ...draft, registrationLink: event.target.value })} placeholder={String(partnerRegistrationLink || '').trim() || 'https://www.myrec.com/...'} />
        </label>
        <label className="partner-field">Emergency contact on site<input value={draft.emergencyContactOnSite || ''} onChange={(event) => setDraft({ ...draft, emergencyContactOnSite: event.target.value })} /></label>
      </div>

      <div className="partner-subsection">
        <h3>Access and arrival</h3>
        <div className="partner-field-grid">
          <label className="partner-field">Parking instructions<textarea value={draft.parkingInstructions || ''} onChange={(event) => setDraft({ ...draft, parkingInstructions: event.target.value })} /></label>
          <label className="partner-field">Arrival procedure<textarea value={draft.arrivalProcedure || ''} onChange={(event) => setDraft({ ...draft, arrivalProcedure: event.target.value })} /></label>
          <label className="partner-field">Dismissal procedure<textarea value={draft.dismissalProcedure || ''} onChange={(event) => setDraft({ ...draft, dismissalProcedure: event.target.value })} /></label>
          <label className="partner-field">Late arrival procedure<textarea value={draft.lateArrivalProcedure || ''} onChange={(event) => setDraft({ ...draft, lateArrivalProcedure: event.target.value })} /></label>
          <label className="partner-field">Hallway rules<textarea value={draft.hallwayRules || ''} onChange={(event) => setDraft({ ...draft, hallwayRules: event.target.value })} /></label>
          <label className="partner-field">Bathroom rules<textarea value={draft.bathroomRules || ''} onChange={(event) => setDraft({ ...draft, bathroomRules: event.target.value })} /></label>
        </div>
      </div>

      <div className="partner-subsection">
        <h3>Technology and network</h3>
        <div className="partner-field-grid">
          <label className="partner-field">Wi-Fi username<input autoComplete="off" value={draft.wifiUsername || ''} onChange={(event) => setDraft({ ...draft, wifiUsername: event.target.value })} /></label>
          <label className="partner-field">
            Wi-Fi password
            <div className="partner-secret">
              <input type={showPassword ? 'text' : 'password'} autoComplete="new-password" value={draft.wifiPassword || ''} onChange={(event) => setDraft({ ...draft, wifiPassword: event.target.value })} />
              <button type="button" onClick={() => setShowPassword((value) => !value)}>{showPassword ? 'Hide' : 'Reveal'}</button>
            </div>
          </label>
          <label className="partner-field">Wi-Fi rules<textarea value={draft.wifiRules || ''} onChange={(event) => setDraft({ ...draft, wifiRules: event.target.value })} /></label>
          <label className="partner-field">Projector/TV instructions<textarea value={draft.projectorConnectionInstructions || ''} onChange={(event) => setDraft({ ...draft, projectorConnectionInstructions: event.target.value })} /></label>
        </div>
        <div className="partner-checkbox-grid" style={{ marginTop: 14 }}>
          <label className="partner-checkbox"><input type="checkbox" checked={Boolean(draft.hasProjectorOrTv)} onChange={(event) => setDraft({ ...draft, hasProjectorOrTv: event.target.checked })} /> TV/projector available</label>
          <label className="partner-checkbox"><input type="checkbox" checked={Boolean(draft.techTestedOnSite)} onChange={(event) => setDraft({ ...draft, techTestedOnSite: event.target.checked })} /> Technology tested on site</label>
          <label className="partner-checkbox"><input type="checkbox" checked={Boolean(draft.sitesWhitelisted)} onChange={(event) => setDraft({ ...draft, sitesWhitelisted: event.target.checked })} /> Required sites whitelisted</label>
          <label className="partner-checkbox"><input type="checkbox" checked={draft.active !== false} onChange={(event) => setDraft({ ...draft, active: event.target.checked })} /> Active for operations</label>
        </div>
      </div>

      <div className="partner-actions partner-location-save-bar">
        <button type="submit" disabled={saving}>{saving ? 'Saving location…' : 'Save location'}</button>
        <button type="button" onClick={() => { setDraft(location); setExpanded(false); }}>Cancel</button>
      </div>
    </form>
  );
}

function ContactsSection(props: {
  partner: Partner;
  contacts: PartnerContact[];
  locations: PartnerLocation[];
  actorId: string | null;
  onChanged: () => Promise<void>;
}): React.ReactElement {
  const { partner, contacts, locations, actorId, onChanged } = props;
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [role, setRole] = useState<ContactRole>('primary');
  const [locationId, setLocationId] = useState('');
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  return (
    <section>
      <div className="partner-section-header">
        <div>
          <h2>Contacts</h2>
          <p>Partner-wide and site-specific contacts for operations, finance, and escalation.</p>
        </div>
      </div>
      {formError && <div role="alert" className="partner-notice partner-notice--error">{formError}</div>}
      <form
        className="partner-inline-create partner-inline-create--contact"
        onSubmit={async (event) => {
          event.preventDefault();
          setSaving(true);
          setFormError(null);
          try {
            await createPartnerContact(partner.id, {
              displayName: name.trim(),
              email: email.trim() || null,
              phone: phone.trim() || null,
              roles: [role],
              locationIds: locationId ? [locationId] : [],
              active: true,
              createdBy: actorId,
              updatedBy: actorId,
            }, actorId);
            setName('');
            setEmail('');
            setPhone('');
            setLocationId('');
            await onChanged();
          } catch (nextError) {
            setFormError(nextError instanceof Error ? nextError.message : 'Failed to add contact.');
          } finally {
            setSaving(false);
          }
        }}
      >
        <input aria-label="Contact name" value={name} onChange={(event) => setName(event.target.value)} placeholder="Full name" required />
        <input aria-label="Contact email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="Email" type="email" />
        <input aria-label="Contact phone" value={phone} onChange={(event) => setPhone(event.target.value)} placeholder="Phone" type="tel" />
        <select aria-label="Contact role" value={role} onChange={(event) => setRole(event.target.value as ContactRole)}>
          {Object.entries(contactRoleLabels).map(([value, labelValue]) => <option key={value} value={value}>{labelValue}</option>)}
        </select>
        <select aria-label="Contact location" value={locationId} onChange={(event) => setLocationId(event.target.value)}>
          <option value="">Partner-wide</option>
          {locations.map((location) => <option key={location.id} value={location.id}>{location.name}</option>)}
        </select>
        <button type="submit" disabled={saving}>{saving ? 'Adding…' : 'Add contact'}</button>
      </form>
      {contacts.length > 0 && <table className="partner-data-table">
        <thead><tr><th>Name</th><th>Role</th><th>Scope</th><th>Contact</th><th>Status</th><th>Actions</th></tr></thead>
        <tbody>
          {contacts.map((contact) => (
            <ContactRow key={contact.id} partnerId={partner.id} contact={contact} locations={locations} actorId={actorId} onChanged={onChanged} />
          ))}
        </tbody>
      </table>}
      {contacts.length === 0 && <div className="partner-empty-state"><strong>No contacts linked</strong><span>Add a primary contact so operators know who to call and billing can be assigned.</span></div>}
    </section>
  );
}

function ContactRow(props: {
  partnerId: string;
  contact: PartnerContact;
  locations: PartnerLocation[];
  actorId: string | null;
  onChanged: () => Promise<void>;
}): React.ReactElement {
  const { partnerId, contact, locations, actorId, onChanged } = props;
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [draft, setDraft] = useState(contact);
  useEffect(() => setDraft(contact), [contact]);

  const handleDelete = async () => {
    const ok = await window.appConfirm(`Delete "${contact.displayName}"? This cannot be undone.`);
    if (!ok) return;
    setDeleting(true);
    try {
      await deletePartnerContact(partnerId, contact.id);
      await onChanged();
    } finally {
      setDeleting(false);
    }
  };

  if (editing) {
    return (
      <tr>
        <td><input aria-label="Contact name" value={draft.displayName} onChange={(event) => setDraft({ ...draft, displayName: event.target.value })} /></td>
        <td>
          <select aria-label="Contact role" value={draft.roles[0] || 'other'} onChange={(event) => setDraft({ ...draft, roles: [event.target.value as ContactRole] })}>
            {Object.entries(contactRoleLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </td>
        <td>
          <select aria-label="Contact scope" value={draft.locationIds?.[0] || ''} onChange={(event) => setDraft({ ...draft, locationIds: event.target.value ? [event.target.value] : [] })}>
            <option value="">Partner-wide</option>
            {locations.map((location) => <option key={location.id} value={location.id}>{location.name}</option>)}
          </select>
        </td>
        <td>
          <input aria-label="Contact email" type="email" value={draft.email || ''} onChange={(event) => setDraft({ ...draft, email: event.target.value || null })} />
          <input aria-label="Contact phone" type="tel" value={draft.phone || ''} onChange={(event) => setDraft({ ...draft, phone: event.target.value || null })} style={{ marginTop: 6 }} />
        </td>
        <td><label className="partner-checkbox"><input type="checkbox" checked={draft.active !== false} onChange={(event) => setDraft({ ...draft, active: event.target.checked })} /> Active</label></td>
        <td>
          <div className="partner-actions partner-actions--compact">
            <button
              type="button"
              className="partner-primary-button"
              disabled={saving}
              onClick={async () => {
                setSaving(true);
                try {
                  await updatePartnerContact(partnerId, contact.id, {
                    displayName: draft.displayName,
                    roles: draft.roles,
                    locationIds: draft.locationIds || [],
                    email: draft.email || null,
                    phone: draft.phone || null,
                    active: draft.active !== false,
                  }, actorId);
                  await onChanged();
                  setEditing(false);
                } finally {
                  setSaving(false);
                }
              }}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button type="button" onClick={() => { setDraft(contact); setEditing(false); }}>Cancel</button>
          </div>
        </td>
      </tr>
    );
  }

  return (
    <tr>
      <td><strong>{contact.displayName}</strong><PhoneCallButton partnerId={partnerId} person={{ type: 'partner_contact', id: contact.id, displayName: contact.displayName }} /></td>
      <td>{contact.roles.map((role) => contactRoleLabels[role] || humanize(role)).join(', ')}</td>
      <td>{(contact.locationIds || []).map((id) => locations.find((location) => location.id === id)?.name || id).join(', ') || 'Partner-wide'}</td>
      <td>{contact.email && <div><a href={`mailto:${contact.email}`}>{contact.email}</a></div>}{contact.phone && <div><a href={`tel:${contact.phone}`}>{contact.phone}</a></div>}{!contact.email && !contact.phone && '—'}</td>
      <td><span className={`partner-pill ${contact.active === false ? 'partner-pill--muted' : 'partner-pill--success'}`}>{contact.active === false ? 'Inactive' : 'Active'}</span></td>
      <td>
        <div className="partner-actions partner-actions--compact">
          <button type="button" onClick={() => setEditing(true)} disabled={deleting}>Edit</button>
          <button type="button" onClick={handleDelete} disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </td>
    </tr>
  );
}

function RequirementsSection(props: {
  partner: Partner;
  requirements: PartnerRequirement[];
  documents: ScopedDocument[];
  clearances: PartnerTeacherClearance[];
  locations: PartnerLocation[];
  actorId: string | null;
  onChanged: () => Promise<void>;
}): React.ReactElement {
  const { partner, requirements, documents, clearances, locations, actorId, onChanged } = props;
  const [creatingRequirement, setCreatingRequirement] = useState(false);
  const [createRequirementDraft, setCreateRequirementDraft] = useState<Omit<PartnerRequirement, 'id'>>({
    partnerId: partner.id, type: 'basic_background_check', label: 'Background check', required: true,
    evidenceRequired: true, instructions: null, estimatedDays: null, validityDays: null,
    accountSiteUrl: null, version: 1, active: true, locationOverrides: {}, createdBy: actorId, updatedBy: actorId,
  });
  const [createRequirementFile, setCreateRequirementFile] = useState<File | null>(null);
  const [createRequirementDocumentTitle, setCreateRequirementDocumentTitle] = useState('');
  const [teacherId, setTeacherId] = useState('');
  const [teachers, setTeachers] = useState<PartnerTeacherCandidate[]>([]);
  const [teachersLoading, setTeachersLoading] = useState(true);
  const [teacherSearch, setTeacherSearch] = useState('');
  const [teacherScope, setTeacherScope] = useState<'nearby' | 'all'>('nearby');
  const [teacherClearanceFilter, setTeacherClearanceFilter] = useState<'all' | 'needs_action' | 'evidence' | 'in_progress' | 'cleared'>('all');
  const [statusByRequirementId, setStatusByRequirementId] = useState<Record<string, RequirementCompletionStatus>>({});
  const [accountCredentialsByRequirementId, setAccountCredentialsByRequirementId] = useState<Record<string, { username: string; password: string }>>({});
  const [savingRequirement, setSavingRequirement] = useState(false);
  const [deletingRequirementId, setDeletingRequirementId] = useState<string | null>(null);
  const [editingRequirementId, setEditingRequirementId] = useState<string | null>(null);
  const [requirementDraft, setRequirementDraft] = useState<PartnerRequirement | null>(null);
  const [requirementFile, setRequirementFile] = useState<File | null>(null);
  const [requirementDocumentTitle, setRequirementDocumentTitle] = useState('');
  const [savingClearance, setSavingClearance] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const geography = useMemo(
    () => derivePartnerGeography(partner.metro, locations.map(location => location.address)),
    [partner.metro, locations],
  );
  const visibleTeachers = useMemo(
    () => filterAndRankTeachers(teachers, geography, teacherSearch, teacherScope),
    [teachers, geography, teacherSearch, teacherScope],
  );
  const activeRequirements = useMemo(
    () => requirements.filter(requirement => requirement.active),
    [requirements],
  );
  const directoryTeachers = useMemo(() => visibleTeachers.filter((teacher) => {
    if (teacherClearanceFilter === 'all') return true;
    const clearance = clearances.find(item => item.teacherId === teacher.id);
    const states = activeRequirements.map(requirement => partnerRequirementDisplayState(
      teacher,
      requirement.type,
      clearance?.requirementStatuses?.[requirement.id]?.status,
    ));
    if (teacherClearanceFilter === 'cleared') return states.length > 0 && states.every(state => state === 'Verified');
    if (teacherClearanceFilter === 'in_progress') return states.some(state => state === 'In progress');
    if (teacherClearanceFilter === 'evidence') return states.some(state => state === 'Evidence on file');
    return states.some(state => state === 'Needs action');
  }), [visibleTeachers, teacherClearanceFilter, clearances, activeRequirements]);
  const selectedTeacher = teachers.find(teacher => teacher.id === teacherId);
  const missingTeacherClearances = clearances.filter(
    clearance => !teachers.some(teacher => teacher.id === clearance.teacherId),
  );
  useEffect(() => {
    let cancelled = false;
    setTeachersLoading(true);
    listActivePartnerTeacherCandidates()
      .then((values) => {
        if (!cancelled) setTeachers(values);
      })
      .catch((nextError) => {
        if (!cancelled) setFormError(nextError instanceof Error ? nextError.message : 'Failed to load teachers.');
      })
      .finally(() => {
        if (!cancelled) setTeachersLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);
  useEffect(() => {
    const existing = clearances.find((clearance) => clearance.teacherId === teacherId);
    const next: Record<string, RequirementCompletionStatus> = {};
    const nextCredentials: Record<string, { username: string; password: string }> = {};
    for (const requirement of requirements) {
      next[requirement.id] = existing?.requirementStatuses?.[requirement.id]?.status || 'not_started';
      nextCredentials[requirement.id] = {
        username: existing?.requirementStatuses?.[requirement.id]?.accountUsername || '',
        password: existing?.requirementStatuses?.[requirement.id]?.accountPassword || '',
      };
    }
    setStatusByRequirementId(next);
    setAccountCredentialsByRequirementId(nextCredentials);
  }, [teacherId, clearances, requirements]);
  const proximityLabel = (teacher: PartnerTeacherCandidate) => {
    const proximity = teacherProximity(teacher, geography);
    if (proximity === 'same_city') return `Near ${teacher.city || teacher.neighborhood || partner.metro}`;
    if (proximity === 'same_state') return `Same state · ${teacher.state}`;
    if (proximity === 'location_unknown') return 'Location not set';
    return [teacher.city, teacher.state].filter(Boolean).join(', ') || teacher.neighborhood || 'Other state';
  };
  const requirementDisplay = (
    teacher: PartnerTeacherCandidate,
    clearance: PartnerTeacherClearance | undefined,
    requirement: PartnerRequirement,
  ): { label: string; tone: string } => {
    const status = clearance?.requirementStatuses?.[requirement.id]?.status;
    const label = partnerRequirementDisplayState(teacher, requirement.type, status);
    if (label === 'Verified') return { label, tone: 'success' };
    if (label === 'In progress') return { label, tone: 'warning' };
    if (label === 'Evidence on file') return { label, tone: 'info' };
    return { label, tone: status === 'rejected' || status === 'expired' ? 'danger' : 'muted' };
  };
  const clearanceEditor = (teacher?: PartnerTeacherCandidate) => (
    <div className="partner-teacher-inline-editor">
      <div className="partner-teacher-editor__header">
        <div>
          <span className="partner-eyebrow">Clearance review</span>
          <h3>{teacher?.displayName || 'Teacher profile unavailable'}</h3>
          <p className="partner-muted">{teacher ? proximityLabel(teacher) : teacherId}</p>
        </div>
        <button className="partner-button partner-button--text" type="button" onClick={() => setTeacherId('')}>Close</button>
      </div>
      {!teacher && <div className="partner-notice partner-notice--warning">This saved clearance points to a missing or deactivated teacher profile.</div>}
      <p className="partner-muted">Global evidence is advisory. Choose Verified only after reviewing this partner&apos;s requirement.</p>
      <form
        onSubmit={async (event) => {
          event.preventDefault();
          setSavingClearance(true);
          setFormError(null);
          const requirementStatuses = Object.fromEntries(requirements.map((requirement) => [
            requirement.id,
            {
              requirementId: requirement.id,
              requirementVersion: requirement.version,
              status: statusByRequirementId[requirement.id] || 'not_started',
              ...(statusByRequirementId[requirement.id] === 'verified'
                ? { completedAt: new Date(), verifiedAt: new Date(), verifiedBy: actorId }
                : {}),
              ...(requirement.type === 'teacher_account' ? {
                accountUsername: accountCredentialsByRequirementId[requirement.id]?.username.trim() || null,
                accountPassword: accountCredentialsByRequirementId[requirement.id]?.password || null,
              } : {}),
            },
          ])) as PartnerTeacherClearance['requirementStatuses'];
          const summary = summarizeTeacherClearance({ requirements, completions: requirementStatuses });
          try {
            await savePartnerTeacherClearance(partner.id, teacherId, {
              ...summary,
              requirementStatuses,
              lastReviewedAt: new Date(),
              lastReviewedBy: actorId,
            }, actorId);
            await onChanged();
          } catch (nextError) {
            setFormError(nextError instanceof Error ? nextError.message : 'Failed to save clearance.');
          } finally {
            setSavingClearance(false);
          }
        }}
      >
        <div className="partner-clearance-grid">
          {activeRequirements.map((requirement) => (
            <div className="partner-field partner-clearance-requirement" key={requirement.id}>
              <span>{requirement.label}</span>
              {requirement.type === 'teacher_account' && requirement.accountSiteUrl && <a href={requirement.accountSiteUrl} target="_blank" rel="noreferrer">Open account site ↗</a>}
              {teacher && globalEvidenceForRequirement(teacher, requirement.type) && <small className="partner-evidence-hint">Global evidence on file</small>}
              {requirement.type === 'teacher_account' && <div className="partner-account-credentials">
                <input aria-label={`${requirement.label} username`} placeholder="Teacher-created username" value={accountCredentialsByRequirementId[requirement.id]?.username || ''} onChange={event => setAccountCredentialsByRequirementId(current => ({ ...current, [requirement.id]: { ...(current[requirement.id] || { password: '' }), username: event.target.value } }))} />
                <input aria-label={`${requirement.label} password`} type="password" autoComplete="new-password" placeholder="Teacher-created password" value={accountCredentialsByRequirementId[requirement.id]?.password || ''} onChange={event => setAccountCredentialsByRequirementId(current => ({ ...current, [requirement.id]: { ...(current[requirement.id] || { username: '' }), password: event.target.value } }))} />
              </div>}
              <select value={statusByRequirementId[requirement.id] || 'not_started'} onChange={(event) => setStatusByRequirementId(current => ({ ...current, [requirement.id]: event.target.value as RequirementCompletionStatus }))}>
                {['not_started', 'in_progress', 'submitted', 'verified', 'rejected', 'expired', 'not_required'].map(status => <option key={status} value={status}>{humanize(status)}</option>)}
              </select>
            </div>
          ))}
        </div>
        <div className="partner-actions">
          <button type="submit" disabled={savingClearance || activeRequirements.length === 0}>{savingClearance ? 'Saving review…' : 'Save clearance review'}</button>
        </div>
      </form>
    </div>
  );
  return (
    <section>
      <div className="partner-section-header">
        <div>
          <h2>Requirements &amp; teachers</h2>
          <p>Define partner-specific requirements and review teacher clearance status.</p>
        </div>
        <span className={`partner-pill ${clearances.length > 0 && clearances.every((clearance) => clearance.cleared) ? 'partner-pill--success' : 'partner-pill--warning'}`}>
          {clearances.filter((clearance) => clearance.cleared).length}/{clearances.length} cleared
        </span>
      </div>
      {formError && <div role="alert" className="partner-notice partner-notice--error">{formError}</div>}

      <div className="partner-section-heading partner-requirements-heading">
        <div><h3>Requirement definitions</h3><p className="partner-muted">Qualifications, checks, training, and partner accounts teachers need.</p></div>
        <button type="button" className="partner-primary-button" onClick={() => setCreatingRequirement(true)}>+ Add requirement</button>
      </div>

      {creatingRequirement && <div className="partner-cal-modal-backdrop" role="presentation" onMouseDown={event => { if (event.target === event.currentTarget && !savingRequirement) setCreatingRequirement(false); }}>
        <div className="partner-cal-modal partner-requirement-modal" role="dialog" aria-modal="true" aria-labelledby="add-requirement-title">
          <button type="button" className="partner-cal-modal__close" aria-label="Close" disabled={savingRequirement} onClick={() => setCreatingRequirement(false)}>×</button>
          <span className="partner-cal-modal__eyebrow">Teacher qualification</span>
          <h3 id="add-requirement-title">Add a requirement</h3>
          <p className="partner-muted">Add the complete requirement now. You can still edit it later.</p>
          <form onSubmit={async event => {
            event.preventDefault();
            setSavingRequirement(true); setFormError(null);
            try {
              const created = await createPartnerRequirement(partner.id, { ...createRequirementDraft, label: createRequirementDraft.label.trim(), instructions: createRequirementDraft.instructions?.trim() || null, accountSiteUrl: createRequirementDraft.type === 'teacher_account' ? createRequirementDraft.accountSiteUrl?.trim() || null : null }, actorId);
              if (createRequirementFile) {
                const documentId = await uploadPartnerDocument({ partnerId: partner.id, file: createRequirementFile, kind: 'notes_attachment', title: createRequirementDocumentTitle.trim() || createRequirementFile.name, actorId, requirementId: created.id });
                await updatePartnerRequirement(partner.id, created.id, { attachmentDocumentIds: [documentId] }, actorId);
              }
              await onChanged(); setCreatingRequirement(false); setCreateRequirementFile(null); setCreateRequirementDocumentTitle('');
              setCreateRequirementDraft({ partnerId: partner.id, type: 'basic_background_check', label: 'Background check', required: true, evidenceRequired: true, instructions: null, estimatedDays: null, validityDays: null, accountSiteUrl: null, version: 1, active: true, locationOverrides: {}, createdBy: actorId, updatedBy: actorId });
            } catch (nextError) { setFormError(nextError instanceof Error ? nextError.message : 'Failed to add requirement.'); }
            finally { setSavingRequirement(false); }
          }}>
            <div className="partner-field-grid">
              <label className="partner-field">Requirement type<select value={createRequirementDraft.type} onChange={event => { const nextType = event.target.value as PartnerRequirementType; setCreateRequirementDraft(current => ({ ...current, type: nextType, label: nextType === 'teacher_account' ? 'Teacher account' : current.label, evidenceRequired: nextType === 'teacher_account' ? false : current.evidenceRequired })); }}>
                {['basic_background_check', 'health_check', 'tb_test', 'cpr', 'epi_pen', 'in_person_background_check', 'fingerprinting', 'state_background_check', 'mandated_reporter', 'concussion_protocol', 'partner_safety_training', 'teacher_account', 'other'].map(value => <option key={value} value={value}>{humanize(value)}</option>)}
              </select></label>
              <label className="partner-field">Requirement name<input autoFocus value={createRequirementDraft.label} onChange={event => setCreateRequirementDraft(current => ({ ...current, label: event.target.value }))} required /></label>
              {createRequirementDraft.type === 'teacher_account' && <label className="partner-field" style={{ gridColumn: '1 / -1' }}>Account website URL<input type="url" value={createRequirementDraft.accountSiteUrl || ''} onChange={event => setCreateRequirementDraft(current => ({ ...current, accountSiteUrl: event.target.value || null }))} placeholder="https://school-portal.example.com" required /><small>Teachers will open this site and create their own username and password.</small></label>}
              <label className="partner-field" style={{ gridColumn: '1 / -1' }}>Instructions<textarea rows={4} value={createRequirementDraft.instructions || ''} onChange={event => setCreateRequirementDraft(current => ({ ...current, instructions: event.target.value || null }))} placeholder="Explain the steps, deadlines, or partner-specific details" /></label>
              <label className="partner-field">Estimated completion time (days)<input type="number" min="0" value={createRequirementDraft.estimatedDays ?? ''} onChange={event => setCreateRequirementDraft(current => ({ ...current, estimatedDays: event.target.value === '' ? null : Number(event.target.value) }))} /></label>
              <label className="partner-field">Validity period (days)<input type="number" min="1" value={createRequirementDraft.validityDays ?? ''} onChange={event => setCreateRequirementDraft(current => ({ ...current, validityDays: event.target.value === '' ? null : Number(event.target.value) }))} placeholder="Never expires" /></label>
              <label className="partner-field">Document title<input value={createRequirementDocumentTitle} onChange={event => setCreateRequirementDocumentTitle(event.target.value)} placeholder="Optional title" /></label>
              <label className="partner-field">Supporting document<input type="file" onChange={event => setCreateRequirementFile(event.target.files?.[0] || null)} /></label>
            </div>
            <div className="partner-requirement-modal__options"><label className="partner-checkbox"><input type="checkbox" checked={createRequirementDraft.required} onChange={event => setCreateRequirementDraft(current => ({ ...current, required: event.target.checked }))} /> Required for clearance</label><label className="partner-checkbox"><input type="checkbox" checked={createRequirementDraft.evidenceRequired === true} onChange={event => setCreateRequirementDraft(current => ({ ...current, evidenceRequired: event.target.checked }))} /> Evidence upload required</label></div>
            <div className="partner-actions partner-requirement-modal__actions"><button type="button" disabled={savingRequirement} onClick={() => setCreatingRequirement(false)}>Cancel</button><button type="submit" className="partner-primary-button" disabled={savingRequirement || !createRequirementDraft.label.trim()}>{savingRequirement ? 'Saving requirement…' : 'Save requirement'}</button></div>
          </form>
        </div>
      </div>}

      {requirements.length > 0 && (
        <table className="partner-data-table">
          <thead><tr><th>Requirement</th><th>Type</th><th>Policy</th><th>Version</th><th>Status</th><th>Actions</th></tr></thead>
          <tbody>
            {requirements.map((requirement) => <React.Fragment key={requirement.id}>
              <tr>
                <td>
                  <strong>{requirement.label}</strong>
                  {requirement.instructions && <div className="partner-help-text">{requirement.instructions}</div>}
                  {requirement.type === 'teacher_account' && requirement.accountSiteUrl && <div className="partner-help-text"><a href={requirement.accountSiteUrl} target="_blank" rel="noreferrer">Open account site ↗</a></div>}
                  {(requirement.attachmentDocumentIds || []).map((documentId) => {
                    const attachment = documents.find((document) => document.id === documentId);
                    return attachment ? <div className="partner-help-text" key={documentId}><a href={attachment.file.url} target="_blank" rel="noreferrer">📎 {attachment.title}</a></div> : null;
                  })}
                </td>
                <td>{humanize(requirement.type)}</td>
                <td>
                  <div>{requirement.required ? 'Required' : 'Optional'} · {requirement.evidenceRequired ? 'Evidence' : 'No evidence'}</div>
                  {(requirement.estimatedDays != null || requirement.validityDays != null) && (
                    <div className="partner-help-text">
                      {requirement.estimatedDays != null ? `${requirement.estimatedDays} day${requirement.estimatedDays === 1 ? '' : 's'} to complete` : 'No completion estimate'}
                      {' · '}
                      {requirement.validityDays != null ? `Valid ${requirement.validityDays} day${requirement.validityDays === 1 ? '' : 's'}` : 'Never expires'}
                    </div>
                  )}
                </td>
                <td>v{requirement.version}</td>
                <td><span className={`partner-pill ${requirement.active ? 'partner-pill--success' : 'partner-pill--muted'}`}>{requirement.active ? 'Active' : 'Inactive'}</span></td>
                <td>
                  <button
                    type="button"
                    onClick={() => {
                      setEditingRequirementId(requirement.id);
                      setRequirementDraft({ ...requirement });
                      setRequirementFile(null);
                      setRequirementDocumentTitle('');
                    }}
                  >
                    Edit
                  </button>{' '}
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        await updatePartnerRequirement(partner.id, requirement.id, { active: !requirement.active }, actorId);
                        await onChanged();
                      } catch (nextError) {
                        setFormError(nextError instanceof Error ? nextError.message : 'Failed to update requirement.');
                      }
                    }}
                  >
                    {requirement.active ? 'Deactivate' : 'Activate'}
                  </button>
                  {' '}<button
                    type="button"
                    className="partner-danger-button"
                    disabled={deletingRequirementId === requirement.id}
                    onClick={async () => {
                      if (!window.confirm(`Delete “${requirement.label}” permanently? It will be removed from this partner's requirement list. This cannot be undone.`)) return;
                      setDeletingRequirementId(requirement.id); setFormError(null);
                      try { await deletePartnerRequirement(partner.id, requirement.id); await onChanged(); }
                      catch (nextError) { setFormError(nextError instanceof Error ? nextError.message : 'Failed to delete requirement.'); }
                      finally { setDeletingRequirementId(null); }
                    }}
                  >{deletingRequirementId === requirement.id ? 'Deleting…' : 'Delete'}</button>
                </td>
              </tr>
              {editingRequirementId === requirement.id && requirementDraft && (
                <tr className="partner-requirement-edit-row">
                  <td colSpan={6}>
                    <div className="partner-record-card">
                      <div className="partner-field-grid">
                        <label className="partner-field">Requirement type<select value={requirementDraft.type} onChange={event => {
                          const nextType = event.target.value as PartnerRequirementType;
                          setRequirementDraft({
                            ...requirementDraft,
                            type: nextType,
                            evidenceRequired: nextType === 'teacher_account' ? false : requirementDraft.evidenceRequired,
                            accountSiteUrl: nextType === 'teacher_account' ? requirementDraft.accountSiteUrl : null,
                          });
                        }}>
                          {['basic_background_check', 'health_check', 'tb_test', 'cpr', 'epi_pen', 'in_person_background_check', 'fingerprinting', 'state_background_check', 'mandated_reporter', 'concussion_protocol', 'partner_safety_training', 'teacher_account', 'other'].map(value => <option key={value} value={value}>{humanize(value)}</option>)}
                        </select></label>
                        <label className="partner-field">Requirement name<input value={requirementDraft.label} onChange={(event) => setRequirementDraft({ ...requirementDraft, label: event.target.value })} /></label>
                        {requirementDraft.type === 'teacher_account' && <label className="partner-field">Account website URL<input type="url" value={requirementDraft.accountSiteUrl || ''} onChange={event => setRequirementDraft({ ...requirementDraft, accountSiteUrl: event.target.value || null })} /></label>}
                        <label className="partner-field" style={{ gridColumn: '1 / -1' }}>Notes and instructions<textarea value={requirementDraft.instructions || ''} onChange={(event) => setRequirementDraft({ ...requirementDraft, instructions: event.target.value || null })} placeholder="Add partner-specific notes, links, steps, or instructions" /></label>
                        <label className="partner-field">Estimated completion time (days)<input type="number" min="0" value={requirementDraft.estimatedDays ?? ''} onChange={event => setRequirementDraft({ ...requirementDraft, estimatedDays: event.target.value === '' ? null : Number(event.target.value) })} /></label>
                        <label className="partner-field">Validity period (days)<input type="number" min="1" value={requirementDraft.validityDays ?? ''} onChange={event => setRequirementDraft({ ...requirementDraft, validityDays: event.target.value === '' ? null : Number(event.target.value) })} placeholder="Never expires" /></label>
                        <label className="partner-field">Document title<input value={requirementDocumentTitle} onChange={(event) => setRequirementDocumentTitle(event.target.value)} placeholder="Optional document title" /></label>
                        <label className="partner-field">Attach document<input type="file" onChange={(event) => setRequirementFile(event.target.files?.[0] || null)} /></label>
                      </div>
                      <div className="partner-requirement-modal__options">
                        <label className="partner-checkbox"><input type="checkbox" checked={requirementDraft.required} onChange={event => setRequirementDraft({ ...requirementDraft, required: event.target.checked })} /> Required for clearance</label>
                        <label className="partner-checkbox"><input type="checkbox" checked={requirementDraft.evidenceRequired === true} disabled={requirementDraft.type === 'teacher_account'} onChange={event => setRequirementDraft({ ...requirementDraft, evidenceRequired: event.target.checked })} /> Evidence upload required</label>
                      </div>
                      {(requirementDraft.attachmentDocumentIds || []).length > 0 && (
                        <div className="partner-subsection">
                          <strong>Attached documents</strong>
                          <div className="partner-document-links">
                            {(requirementDraft.attachmentDocumentIds || []).map((documentId) => {
                              const attachment = documents.find((document) => document.id === documentId);
                              return attachment
                                ? <a key={documentId} href={attachment.file.url} target="_blank" rel="noreferrer">{attachment.title}</a>
                                : <span key={documentId}>Document unavailable</span>;
                            })}
                          </div>
                        </div>
                      )}
                      <div className="partner-actions">
                        <button type="button" className="partner-primary-button" disabled={savingRequirement || !requirementDraft.label.trim()} onClick={async () => {
                          setSavingRequirement(true);
                          setFormError(null);
                          try {
                            let attachmentDocumentIds = [...(requirementDraft.attachmentDocumentIds || [])];
                            if (requirementFile) {
                              const documentId = await uploadPartnerDocument({
                                partnerId: partner.id,
                                file: requirementFile,
                                kind: 'notes_attachment',
                                title: requirementDocumentTitle.trim() || requirementFile.name,
                                actorId,
                                requirementId: requirement.id,
                              });
                              attachmentDocumentIds = Array.from(new Set([...attachmentDocumentIds, documentId]));
                            }
                            await updatePartnerRequirement(partner.id, requirement.id, {
                              type: requirementDraft.type,
                              label: requirementDraft.label.trim(),
                              required: requirementDraft.required,
                              evidenceRequired: requirementDraft.type === 'teacher_account' ? false : requirementDraft.evidenceRequired === true,
                              instructions: requirementDraft.instructions?.trim() || null,
                              estimatedDays: requirementDraft.estimatedDays ?? null,
                              validityDays: requirementDraft.validityDays ?? null,
                              accountSiteUrl: requirementDraft.type === 'teacher_account' ? requirementDraft.accountSiteUrl?.trim() || null : null,
                              attachmentDocumentIds,
                              version: requirement.version + 1,
                            }, actorId);
                            await onChanged();
                            setEditingRequirementId(null);
                            setRequirementDraft(null);
                            setRequirementFile(null);
                            setRequirementDocumentTitle('');
                          } catch (nextError) {
                            setFormError(nextError instanceof Error ? nextError.message : 'Failed to save requirement.');
                          } finally {
                            setSavingRequirement(false);
                          }
                        }}>{savingRequirement ? 'Saving requirement…' : 'Save requirement'}</button>
                        <button type="button" disabled={savingRequirement} onClick={() => { setEditingRequirementId(null); setRequirementDraft(null); setRequirementFile(null); setRequirementDocumentTitle(''); }}>Cancel</button>
                      </div>
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>)}
          </tbody>
        </table>
      )}
      {requirements.length === 0 && <div className="partner-empty-state"><strong>No requirements defined</strong><span>Add the checks or learning teachers must complete for this partner.</span></div>}

      <div className="partner-subsection">
        <div className="partner-section-heading">
          <div><h3>Teacher discovery and clearance</h3><p className="partner-muted">Find active teachers by geography and review what they need to teach here.</p></div>
          <span className="partner-count">{teachers.length} active teachers</span>
        </div>
        <div className="partner-teacher-controls">
          <label className="partner-field partner-teacher-search">
            <span>Search by teacher name</span>
            <input type="search" value={teacherSearch} onChange={(event) => setTeacherSearch(event.target.value)} placeholder="Search all active teachers" />
          </label>
          <label className="partner-field">
            <span>Directory</span>
            <select value={teacherScope} onChange={(event) => setTeacherScope(event.target.value as 'nearby' | 'all')}>
              <option value="nearby">Nearby</option>
              <option value="all">All active teachers</option>
            </select>
          </label>
          <div className="partner-field partner-teacher-filter">
            <span>Clearance status</span>
            <div className="partner-segmented-control">
              {([
                ['all', 'All'],
                ['needs_action', 'Needs action'],
                ['evidence', 'Evidence ready'],
                ['in_progress', 'In progress'],
                ['cleared', 'Cleared'],
              ] as const).map(([value, text]) => (
                <button
                  type="button"
                  className={teacherClearanceFilter === value ? 'is-active' : ''}
                  aria-pressed={teacherClearanceFilter === value}
                  onClick={() => setTeacherClearanceFilter(value)}
                  key={value}
                >
                  {text}
                </button>
              ))}
            </div>
          </div>
        </div>
        {teacherSearch.trim() && teacherScope === 'nearby' && <p className="partner-muted">Name search includes active teachers outside the nearby directory.</p>}
        {missingTeacherClearances.length > 0 && (
          <div className="partner-notice partner-notice--warning">
            <strong>{missingTeacherClearances.length} saved clearance {missingTeacherClearances.length === 1 ? 'record has' : 'records have'} no active teacher profile.</strong>
            <span> The teacher may be deactivated or missing. Existing records are retained.</span>
            <div className="partner-inline-actions">
              {missingTeacherClearances.map(clearance => (
                <button className="partner-button partner-button--secondary" type="button" key={clearance.teacherId} onClick={() => setTeacherId(current => current === clearance.teacherId ? '' : clearance.teacherId)}>
                  Review {clearance.teacherId}
                </button>
              ))}
            </div>
            {teacherId && !selectedTeacher && clearanceEditor()}
          </div>
        )}
        <div className="partner-teacher-workspace">
          <div className="partner-teacher-directory">
            <div className="partner-teacher-directory__summary">
              <strong>{directoryTeachers.length} teacher{directoryTeachers.length === 1 ? '' : 's'}</strong>
              <span className="partner-muted">Only city/state is shown. Private addresses stay hidden.</span>
            </div>
            {teachersLoading ? (
              <div className="partner-empty-state">Loading active teachers...</div>
            ) : directoryTeachers.length === 0 ? (
              <div className="partner-empty-state">No active teachers match these search, location, and clearance filters.</div>
            ) : directoryTeachers.map((teacher) => {
              const clearance = clearances.find(item => item.teacherId === teacher.id);
              const qualifications = [
                ['Background check', teacher.backgroundCheckCleared || Boolean(teacher.overtureBgCheckFile)],
                ['Mandated reporter', Boolean(teacher.mandatoryReportingCertificationFile)],
                ['Overture Teaching Certification', teacher.ogTrainingComplete],
              ] as const;
              const requirementStates = activeRequirements.map(requirement => requirementDisplay(teacher, clearance, requirement));
              const verifiedCount = requirementStates.filter(display => display.label === 'Verified').length;
              const evidenceCount = requirementStates.filter(display => display.label === 'Evidence on file').length;
              const actionCount = requirementStates.filter(display => display.label === 'Needs action').length;
              const inProgressCount = requirementStates.filter(display => display.label === 'In progress').length;
              const expanded = teacher.id === teacherId;
              return (
                <article className={`partner-teacher-accordion${expanded ? ' partner-teacher-accordion--expanded' : ''}`} key={teacher.id}>
                  <button
                    className="partner-teacher-accordion__trigger"
                    type="button"
                    aria-expanded={expanded}
                    onClick={() => setTeacherId(current => current === teacher.id ? '' : teacher.id)}
                  >
                    <div className="partner-teacher-identity">
                      {teacher.headshotUrl ? <img src={teacher.headshotUrl} alt="" /> : <span>{teacher.displayName.slice(0, 1).toUpperCase()}</span>}
                      <div><strong>{teacher.displayName}</strong><p>{proximityLabel(teacher)}</p></div>
                    </div>
                    <div className="partner-teacher-accordion__subjects">
                      {teacher.subjects.length > 0
                        ? teacher.subjects.slice(0, 3).map(subject => <span className="partner-pill" key={subject}>{humanize(subject)}</span>)
                        : <span className="partner-muted">No subjects</span>}
                    </div>
                    <div className="partner-teacher-accordion__qualifications" aria-label="Global qualifications">
                      {qualifications.filter(([, available]) => available).map(([qualification]) => (
                        <span className="partner-pill partner-pill--success" key={qualification}>✓ {qualification}</span>
                      ))}
                      {qualifications.every(([, available]) => !available) && <span className="partner-muted">No global evidence</span>}
                    </div>
                    <div className="partner-teacher-accordion__summary">
                      {verifiedCount > 0 && <span className="partner-pill partner-pill--success">{verifiedCount} verified</span>}
                      {inProgressCount > 0 && <span className="partner-pill partner-pill--warning">{inProgressCount} in progress</span>}
                      {evidenceCount > 0 && <span className="partner-pill partner-pill--info">{evidenceCount} evidence ready</span>}
                      {actionCount > 0 && <span className="partner-pill partner-pill--muted">{actionCount} need action</span>}
                      {activeRequirements.length === 0 && <span className="partner-muted">No active requirements</span>}
                    </div>
                    <span className="partner-teacher-accordion__chevron" aria-hidden="true">{expanded ? '−' : '+'}</span>
                  </button>
                  {expanded && clearanceEditor(teacher)}
                </article>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

function BillingSection(props: {
  partner: Partner;
  squads: Squad[];
  contacts: PartnerContact[];
  documents: ScopedDocument[];
  saving: boolean;
  actorId: string | null;
  onSave: (patch: Partial<Partner>) => Promise<boolean>;
  onChanged: () => Promise<void>;
  onManageContacts: () => void;
}): React.ReactElement {
  const { partner, squads, contacts, documents, actorId, onSave, onChanged, onManageContacts } = props;
  const navigate = useNavigate();
  const withBillingDefaults = (defaults: Partner['billingDefaults']) => ({
    ...(defaults || {}),
    netPaymentDays: defaults?.netPaymentDays ?? 14,
    invoiceTiming: defaults?.invoiceTiming ?? 'biweekly' as const,
  });
  const [draft, setDraft] = useState(withBillingDefaults(partner.billingDefaults));
  const [autosaveState, setAutosaveState] = useState<'saved' | 'saving' | 'error'>('saved');
  const [setupExpanded, setSetupExpanded] = useState(false);
  const lastSavedBillingRef = useRef(JSON.stringify(withBillingDefaults(partner.billingDefaults)));
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [invoicesLoading, setInvoicesLoading] = useState(true);
  const [creatingInvoice, setCreatingInvoice] = useState(false);
  const [invoiceActionMessage, setInvoiceActionMessage] = useState<string | null>(null);
  const invoiceTerms = useMemo(() => listProgramTerms({ startYear: new Date().getFullYear() - 1, years: 4 }), []);
  const defaultInvoiceTermId = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    const upcoming = squads
      .map((squad) => ({ squad, start: String(squad.schedule?.mainDate || squad.schedule?.startDate || '') }))
      .filter((item) => item.start && item.start >= today)
      .sort((a, b) => a.start.localeCompare(b.start))[0]?.squad;
    return (upcoming && resolveSquadTermId(upcoming)) || programTermForDate(new Date()).id;
  }, [squads]);
  const [invoiceTermId, setInvoiceTermId] = useState(defaultInvoiceTermId);
  const [defaultPriceInput, setDefaultPriceInput] = useState(
    partner.billingDefaults?.defaultProgramPriceCents == null
      ? ''
      : (partner.billingDefaults.defaultProgramPriceCents / 100).toFixed(2)
  );
  const [documentKind, setDocumentKind] = useState<PartnerDocumentKind>('coi');
  const [documentTitle, setDocumentTitle] = useState('');
  const [documentFile, setDocumentFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  useEffect(() => {
    setInvoicesLoading(true);
    return onSnapshot(query(collection(db, 'invoices'), where('partnerId', '==', partner.id)), (snapshot) => {
      const next = snapshot.docs.map((item) => ({ id: item.id, ...(item.data() as any) } as Invoice)).filter((invoice) => invoice.legacy !== true && !(invoice as any).supersededAt);
      next.sort(comparePartnerInvoices);
      setInvoices(next);
      setInvoicesLoading(false);
    }, () => {
      setInvoicesLoading(false);
    });
  }, [partner.id]);
  useEffect(() => {
    setDraft(withBillingDefaults(partner.billingDefaults));
    setDefaultPriceInput(
      partner.billingDefaults?.defaultProgramPriceCents == null
        ? ''
        : (partner.billingDefaults.defaultProgramPriceCents / 100).toFixed(2)
    );
    lastSavedBillingRef.current = JSON.stringify(withBillingDefaults(partner.billingDefaults));
    setAutosaveState('saved');
  }, [partner.id]);
  useEffect(() => { setInvoiceTermId(defaultInvoiceTermId); }, [defaultInvoiceTermId, partner.id]);
  const createPartnerInvoice = async () => {
    setCreatingInvoice(true);
    try {
      const today = new Date().toISOString().slice(0, 10);
      const netPaymentDays = Number.isFinite(Number(draft.netPaymentDays)) ? Number(draft.netPaymentDays) : 14;
      const due = new Date(`${today}T00:00:00Z`);
      due.setUTCDate(due.getUTCDate() + netPaymentDays);
      const created = await addDoc(collection(db, 'invoices'), {
        legacy: false,
        invoiceKind: 'adhoc',
        partnerId: partner.id,
        termId: invoiceTermId,
        invoiceDate: today,
        invoiceSendDate: today,
        paymentDueDate: due.toISOString().slice(0, 10),
        schoolNameSnapshot: partner.name,
        schoolTermSnapshot: programTermLabelFromId(invoiceTermId),
        payerLegalNameSnapshot: draft.legalName || partner.name,
        payerBillingAddressSnapshot: draft.billingAddress || {},
        netPaymentSnapshot: netPaymentDays,
        paymentTermsSource: 'partner_default',
        paymentDueDateSource: 'terms',
        revision: 0,
        coveredSquadIds: [],
        lineItems: [],
        subtotalCents: 0,
        totalCents: 0,
        currency: 'USD',
        status: 'draft',
        createdAt: serverTimestamp(),
        createdBy: actorId || null,
        updatedAt: serverTimestamp(),
        updatedBy: actorId || null,
      });
      navigate(`/app/admin/invoices/${created.id}`);
    } finally {
      setCreatingInvoice(false);
    }
  };
  const requestInvoiceRecompute = useCallback(async (seasonIds: string[]) => {
    setInvoiceActionMessage(null);
    try {
      const recompute = httpsCallable<{ partnerId: string; seasonId: string }, { ok: boolean }>(getFunctions(), 'recomputePartnerSeasonInvoices');
      await Promise.all(Array.from(new Set(seasonIds.filter(Boolean))).map((seasonId) => recompute({ partnerId: partner.id, seasonId })));
      setInvoiceActionMessage('Scheduled invoice drafts are up to date.');
    } catch (nextError) {
      const code = String((nextError as any)?.code || '');
      setInvoiceActionMessage(code === 'functions/internal' || code === 'functions/not-found' || code === 'functions/unavailable'
        ? 'Invoice settings were saved, but the invoice rebuild function is not deployed yet.'
        : nextError instanceof Error ? nextError.message : 'Scheduled invoices could not be generated.');
    }
  }, [partner.id]);
  const canonicalContactId = (storedId?: string | null): string => {
    if (!storedId) return '';
    return contacts.find((contact) =>
      contact.id === storedId || contact.legacyContactIds?.includes(storedId)
    )?.id || storedId;
  };
  const authorizedContactIds = new Set(
    [...(draft.authorizedContactIds || []), draft.billingContactId || ''].map((contactId) => canonicalContactId(contactId)).filter(Boolean)
  );
  useEffect(() => {
    if (contacts.length !== 1 || draft.billingContactId) return;
    const onlyContact = contacts[0];
    setDraft((current) => ({ ...current, billingContactId: onlyContact.id, authorizedContactIds: [onlyContact.id] }));
  }, [contacts, draft.billingContactId]);
  const setBillingAddressField = (
    field: 'line1' | 'line2' | 'city' | 'state' | 'postalCode' | 'country',
    value: string
  ) => {
    setDraft({
      ...draft,
      billingAddress: {
        ...(draft.billingAddress || {}),
        [field]: value,
      },
    });
  };
  const toggleAuthorizedContact = (contactId: string, checked: boolean) => {
    const next = new Set(authorizedContactIds);
    if (checked) next.add(contactId);
    else next.delete(contactId);
    setDraft({ ...draft, authorizedContactIds: Array.from(next) });
  };
  const requiresSupportingDocument = Boolean(partner.vendorProcess?.required);
  const billingChecks = [
    { label: 'Invoice timing', complete: Boolean(draft.invoiceTiming), target: 'billing-timing' },
    { label: 'Payment terms', complete: Number.isFinite(Number(draft.netPaymentDays)), target: 'billing-timing' },
    { label: 'Legal billing name', complete: Boolean(draft.legalName), target: 'billing-recipient' },
    { label: 'Payment mode', complete: Boolean(draft.paymentMode), target: 'billing-pricing' },
    { label: 'Payment preference', complete: Boolean(draft.paymentPreference), target: 'billing-recipient' },
    { label: 'Primary billing contact', complete: Boolean(draft.billingContactId), target: 'billing-contacts' },
    { label: 'Billing address', complete: Boolean(draft.billingAddress?.line1 && draft.billingAddress?.city && draft.billingAddress?.state), target: 'billing-address' },
    ...(requiresSupportingDocument ? [{ label: 'Supporting document', complete: documents.length > 0, target: 'billing-documents' }] : []),
    { label: 'Revenue share details', complete: !draft.revenueShare?.enabled || (
      Number.isFinite(Number(draft.revenueShare.overturePercent))
      && Number(draft.revenueShare.overturePercent) > 0
      && Number(draft.revenueShare.overturePercent) < 100
      && Boolean(draft.revenueShare.chargeBasis)
    ), target: 'billing-advanced' },
  ];
  const billingReadyCount = billingChecks.filter((item) => item.complete).length;
  const billingSetupComplete = billingReadyCount === billingChecks.length;
  const autosaveLabel = autosaveState === 'saving' ? 'Saving…' : autosaveState === 'error' ? 'Save failed' : 'Saved ✓';

  useEffect(() => {
    const normalizedDraft = {
      ...draft,
      billingContactId: canonicalContactId(draft.billingContactId) || null,
      authorizedContactIds: Array.from(authorizedContactIds),
    };
    const serialized = JSON.stringify(normalizedDraft);
    if (serialized === lastSavedBillingRef.current) return;
    const previousDefaults = JSON.parse(lastSavedBillingRef.current || '{}');
    const previousTiming = previousDefaults.invoiceTiming;
    const previousNetPaymentDays = Number(previousDefaults.netPaymentDays ?? 14);
    setAutosaveState('saving');
    const timer = window.setTimeout(async () => {
      const saved = await onSave({ billingDefaults: normalizedDraft });
      if (!saved) {
        setAutosaveState('error');
        return;
      }
      lastSavedBillingRef.current = JSON.stringify(normalizedDraft);
      setAutosaveState('saved');
      if (previousTiming !== normalizedDraft.invoiceTiming || previousNetPaymentDays !== Number(normalizedDraft.netPaymentDays ?? 14)) {
        const affectedSeasonIds = squads.map((squad) => String(resolveSquadTermId(squad) || '')).filter(Boolean);
        setInvoiceActionMessage('Invoice settings saved. Updating unsent drafts…');
        await requestInvoiceRecompute(affectedSeasonIds.length ? affectedSeasonIds : [invoiceTermId]);
      }
      const defaultPrice = Number(normalizedDraft.defaultProgramPriceCents);
      if (!Number.isFinite(defaultPrice) || defaultPrice < 0) return;
      const inheritingSquads = squads.filter((squad) => {
        const item = squad as any;
        const hasExplicitPrice = [item.tuitionPerStudentPerClassCents, item.flatFeePerClassCents, item.priceCents]
          .some((value) => typeof value === 'number' && Number.isFinite(value));
        return item.priceSource === 'partner_default' || !hasExplicitPrice;
      });
      for (let offset = 0; offset < inheritingSquads.length; offset += 450) {
        const batch = writeBatch(db);
        inheritingSquads.slice(offset, offset + 450).forEach((squad) => {
          const mode = (squad as any).paymentMode || normalizedDraft.paymentMode || 'stripe';
          const pricePatch = mode === 'school_flat_fee'
            ? { flatFeePerClassCents: Math.round(defaultPrice), priceCents: Math.round(defaultPrice) }
            : { tuitionPerStudentPerClassCents: Math.round(defaultPrice), priceCents: Math.round(defaultPrice) };
          batch.update(doc(db, 'squads', squad.id), { ...pricePatch, priceSource: 'partner_default', updatedAt: serverTimestamp(), updatedBy: actorId || null });
        });
        await batch.commit();
      }
    }, 700);
    return () => window.clearTimeout(timer);
  }, [actorId, authorizedContactIds, draft, invoiceTermId, onSave, requestInvoiceRecompute, squads]);
  return (
    <section>
      <div className="partner-section-header">
        <div>
          <h2>Billing &amp; documents</h2>
          <p>Payment setup, invoice terms, authorized contacts, and partner-level compliance files.</p>
        </div>
        <span className={`partner-pill ${billingReadyCount === billingChecks.length ? 'partner-pill--success' : 'partner-pill--warning'}`}>{billingReadyCount}/{billingChecks.length} ready</span>
      </div>
      <div className="partner-invoice-workspace">
        <div className="partner-invoice-workspace__header">
          <div><span className="partner-invoice-workspace__eyebrow">Partner invoicing</span><h3>Create, review, and send</h3><p>Invoices here cover this partner across all locations. Sending always requires operator review.</p></div>
          <div className="partner-invoice-workspace__create">
            <label>Invoice season<select value={invoiceTermId} onChange={(event) => setInvoiceTermId(event.target.value)}>{invoiceTerms.map((term) => <option key={term.id} value={term.id}>{term.label}</option>)}</select></label>
            <button type="button" className="partner-primary-button" onClick={() => void createPartnerInvoice()} disabled={creatingInvoice}>{creatingInvoice ? 'Creating…' : '+ Create invoice'}</button>
          </div>
        </div>
        {invoiceActionMessage && <div className="partner-invoice-workspace__message" role="status">{invoiceActionMessage}</div>}
        <div className="partner-invoice-workspace__list">
          {invoicesLoading ? <p className="partner-muted">Loading invoices…</p> : invoices.length === 0 ? <div className="partner-invoice-empty"><strong>No invoices yet</strong><span>Create the first draft for this partner, then review its squads, generate the PDF, and send it.</span></div> : invoices.map((invoice) => {
            const status = deriveInvoiceStatus(invoice);
            const total = new Intl.NumberFormat(undefined, { style: 'currency', currency: invoice.currency || 'USD' }).format(Number(invoice.totalCents || 0) / 100);
            const lineItems = invoice.lineItems || [];
            const claimedDates = Array.from(new Set(lineItems.flatMap((line: any) => Array.isArray(line.classDates) ? line.classDates : []))).sort();
            const claimedSessionCount = lineItems.reduce((count: number, line: any) => count + (Array.isArray(line.classDates) ? line.classDates.length : 0), 0);
            const claimedSquadCount = new Set([
              ...((invoice as any).coveredSquadIds || []),
              ...lineItems.map((line: any) => line.squadId),
            ].map((squadId: any) => String(squadId || '').trim()).filter(Boolean)).size;
            const claimedSummary = claimedSessionCount
              ? `${claimedSessionCount} class ${claimedSessionCount === 1 ? 'session' : 'sessions'} across ${claimedSquadCount} ${claimedSquadCount === 1 ? 'squad' : 'squads'} · ${claimedDates.length} class ${claimedDates.length === 1 ? 'date' : 'dates'} · ${claimedDates[0]}${claimedDates.length > 1 ? ` – ${claimedDates[claimedDates.length - 1]}` : ''}`
              : 'No class sessions claimed';
            const plannedDate = String((invoice as any).plannedSendDate || invoice.invoiceSendDate || invoice.invoiceDate || '').slice(0, 10);
            const relationship = (invoice as any).replacesInvoiceId
              ? `Replacement for ${(invoice as any).replacesInvoiceId}`
              : (invoice as any).replacedByInvoiceId
                ? `Replaced by ${(invoice as any).replacedByInvoiceId}`
                : null;
            return <button type="button" className="partner-invoice-row" key={invoice.id} onClick={() => navigate(`/app/admin/invoices/${invoice.id}`)}>
              <span className={`partner-invoice-status partner-invoice-status--${status}`}>{status.replace(/_/g, ' ')}</span>
              <span><strong>{invoice.internalInvoiceNumberSnapshot || programTermLabelFromId(invoice.termId || '') || 'Invoice draft'}</strong><small>{relationship || claimedSummary}</small></span>
              <span><strong>{total}</strong><small>{invoice.sentAt ? `Sent ${invoice.invoiceDate || ''}` : plannedDate ? `Planned ${plannedDate}` : 'Planned date missing'}</small></span>
              <b>{status === 'canceled' ? 'View canceled invoice →' : invoice.sentAt ? 'Open invoice →' : 'Review & send →'}</b>
            </button>;
          })}
        </div>
      </div>
      {billingReadyCount < billingChecks.length && (
        <div className="partner-billing-readiness">
          <div><strong>Billing setup needs attention</strong><span>{billingChecks.length - billingReadyCount} items remaining</span></div>
          <div className="partner-billing-readiness__items">{billingChecks.map((item) => <a key={item.label} href={`#${item.target}`} className={item.complete ? 'is-complete' : ''}><b>{item.complete ? '✓' : '○'}</b>{item.label}</a>)}</div>
        </div>
      )}
      {billingSetupComplete && !setupExpanded ? (
        <div className="partner-billing-summary">
          <div><strong>Billing setup complete</strong><span>{draft.invoiceTiming === 'biweekly' ? 'Biweekly' : humanize(draft.invoiceTiming || '')} · Net {draft.netPaymentDays ?? 14} · {draft.paymentPreference ? humanize(draft.paymentPreference) : 'Payment method missing'}</span></div>
          <span className={`partner-autosave-state partner-autosave-state--${autosaveState}`}>{autosaveLabel}</span>
          <button type="button" onClick={() => setSetupExpanded(true)}>Edit setup</button>
        </div>
      ) : <>
      <div className="partner-billing-section" id="billing-timing"><div className="partner-billing-section__heading"><span>1</span><div><h3>Invoice schedule</h3><p>Decide when invoices are prepared and when payment is due.</p></div><small className={`partner-autosave-state partner-autosave-state--${autosaveState}`}>{autosaveLabel}</small></div><div className="partner-billing-fields">
        <label className="partner-field">Invoice timing<small>Controls when partner-wide invoice drafts become ready</small><select value={draft.invoiceTiming || ''} onChange={(event) => setDraft({ ...draft, invoiceTiming: (event.target.value || null) as any })}><option value="">Select timing…</option><option value="beginning">Beginning — first scheduled class</option><option value="biweekly">Biweekly — prior two weeks delivered</option><option value="middle_and_end">Middle and end — value already delivered</option><option value="end">End — after the final class</option></select></label>
        <label className="partner-field">Payment terms<small>Standard is Net 14; override only when required</small><div className="partner-input-affix"><span>Net</span><input type="number" min="0" value={draft.netPaymentDays ?? 14} onChange={(event) => setDraft({ ...draft, netPaymentDays: event.target.value === '' ? 14 : Number(event.target.value) })} /></div></label>
      </div></div>

      <div className="partner-billing-section" id="billing-recipient"><div className="partner-billing-section__heading"><span>2</span><div><h3>Who and where to bill</h3><p>Set the invoice identity, delivery method, primary contact, and address.</p></div><small className={`partner-autosave-state partner-autosave-state--${autosaveState}`}>{autosaveLabel}</small></div><div className="partner-billing-fields">
        <label className="partner-field">Legal billing name<input value={draft.legalName || ''} onChange={(event) => setDraft({ ...draft, legalName: event.target.value })} /></label>
        <label className="partner-field">Payment preference<select className={!draft.paymentPreference ? 'partner-select--incomplete' : ''} value={draft.paymentPreference || ''} onChange={(event) => setDraft({ ...draft, paymentPreference: (event.target.value || null) as any })}><option value="">Select payment preference…</option><option value="check">Check</option><option value="ach">ACH</option><option value="card">Card</option><option value="portal">Portal</option><option value="other">Other</option></select></label>
      </div>
      <div className="partner-billing-contact-list" id="billing-contacts"><div className="partner-billing-contact-list__header"><strong>Billing contacts</strong><span>Select everyone who receives invoices and choose one primary contact.</span><button type="button" onClick={onManageContacts}>Add or edit contacts</button></div>{contacts.length === 0 ? <div className="partner-empty-state"><strong>No contacts available</strong><span>Add a partner contact before configuring invoicing.</span><button type="button" onClick={onManageContacts}>Add billing contact</button></div> : contacts.map((contact) => {
        const isPrimary = canonicalContactId(draft.billingContactId) === contact.id;
        const isAuthorized = authorizedContactIds.has(contact.id);
        return <div className="partner-billing-contact" key={contact.id}><label><input type="checkbox" checked={isAuthorized} onChange={(event) => toggleAuthorizedContact(contact.id, event.target.checked)} /><span><strong>{contact.displayName}</strong><small>{contact.email || 'No email'}</small></span></label><label className="partner-billing-contact__primary"><input type="radio" name="primary-billing-contact" checked={isPrimary} onChange={() => setDraft((current) => ({ ...current, billingContactId: contact.id, authorizedContactIds: Array.from(new Set([...(current.authorizedContactIds || []), contact.id])) }))} /> Primary</label></div>;
      })}</div>
      <div className="partner-billing-address" id="billing-address"><label className="partner-field">Address line 1<input value={draft.billingAddress?.line1 || ''} onChange={(event) => setBillingAddressField('line1', event.target.value)} /></label><label className="partner-field">Address line 2<input value={draft.billingAddress?.line2 || ''} onChange={(event) => setBillingAddressField('line2', event.target.value)} /></label><label className="partner-field">City<input value={draft.billingAddress?.city || ''} onChange={(event) => setBillingAddressField('city', event.target.value)} /></label><label className="partner-field">State<input value={draft.billingAddress?.state || ''} onChange={(event) => setBillingAddressField('state', event.target.value.toUpperCase())} maxLength={2} /></label><label className="partner-field">ZIP / postal code<input value={draft.billingAddress?.postalCode || ''} onChange={(event) => setBillingAddressField('postalCode', event.target.value)} /></label><label className="partner-field">Country<input value={draft.billingAddress?.country || ''} onChange={(event) => setBillingAddressField('country', event.target.value)} /></label></div>
      </div>

      <div className="partner-billing-section" id="billing-pricing"><div className="partner-billing-section__heading"><span>3</span><div><h3>Pricing defaults</h3><p>Future squads inherit these values unless a squad overrides them.</p></div><small className={`partner-autosave-state partner-autosave-state--${autosaveState}`}>{autosaveLabel}</small></div><div className="partner-billing-fields">
        <label className="partner-field">Default squad payment mode<select value={draft.paymentMode || ''} onChange={(event) => setDraft({ ...draft, paymentMode: (event.target.value || null) as any })}><option value="">Unspecified</option><option value="stripe">Stripe</option><option value="school_flat_fee">Partner flat fee</option><option value="school_enrollment">Partner enrollment</option></select></label>
        <label className="partner-field">Default program price<small>{draft.paymentMode === 'school_flat_fee' ? 'Per class' : 'Per student, per hour'}</small><input type="number" min="0" step="0.01" placeholder="0.00" value={defaultPriceInput} onChange={(event) => {
          const value = event.target.value;
          setDefaultPriceInput(value);
          const dollars = Number(value);
          setDraft((current) => ({
            ...current,
            defaultProgramPriceCents: value === '' || !Number.isFinite(dollars)
              ? null
              : Math.round(dollars * 100),
          }));
        }} /></label>
      </div></div>

      <details className="partner-billing-advanced" id="billing-advanced">
        <summary><span><strong>Advanced billing options</strong><small>Revenue share and prorating</small></span><span className={`partner-autosave-state partner-autosave-state--${autosaveState}`}>{autosaveLabel}</span><b>⌄</b></summary>
        <div className="partner-billing-advanced__body"><h3>Revenue share</h3>
        <p className="partner-section-intro">Define the split and whether the configured price is Overture’s net price or the parent-facing gross price.</p>
        <label className="partner-checkbox">
          <input
            type="checkbox"
            checked={Boolean(draft.revenueShare?.enabled)}
            onChange={(event) => setDraft({
              ...draft,
              revenueShare: {
                enabled: event.target.checked,
                overturePercent: draft.revenueShare?.overturePercent ?? 70,
                chargeBasis: draft.revenueShare?.chargeBasis || 'partner_markup',
              },
            })}
          />
          This partner uses revenue sharing
        </label>
        {draft.revenueShare?.enabled && (
          <div className="partner-field-grid">
            <label className="partner-field">
              Overture share (%)
              <small>The partner automatically receives the remaining percentage</small>
              <input
                type="number"
                min="1"
                max="99"
                step="0.01"
                value={draft.revenueShare.overturePercent ?? ''}
                onChange={(event) => setDraft({
                  ...draft,
                  revenueShare: {
                    ...draft.revenueShare!,
                    overturePercent: event.target.value === '' ? null : Math.min(99, Math.max(1, Number(event.target.value))),
                  },
                })}
              />
            </label>
            <label className="partner-field">
              Partner share (%)
              <small>Calculated from the Overture share</small>
              <input
                type="number"
                readOnly
                value={draft.revenueShare.overturePercent == null ? '' : Number((100 - Number(draft.revenueShare.overturePercent)).toFixed(2))}
              />
            </label>
            <label className="partner-field" style={{ gridColumn: '1 / -1' }}>
              How is the configured price treated?
              <select
                value={draft.revenueShare.chargeBasis || ''}
                onChange={(event) => setDraft({
                  ...draft,
                  revenueShare: {
                    ...draft.revenueShare!,
                    chargeBasis: (event.target.value || null) as 'partner_markup' | 'partner_share_deducted' | null,
                  },
                })}
              >
                <option value="">Select…</option>
                <option value="partner_markup">Partner adds its margin on top of Overture’s price</option>
                <option value="partner_share_deducted">Partner share comes out of the parent-facing price</option>
              </select>
            </label>
            {draft.revenueShare.overturePercent != null && draft.revenueShare.chargeBasis && (
              <div className="partner-notice" style={{ gridColumn: '1 / -1' }}>
                <span>
                  <strong>Example using a $100 configured price:</strong>{' '}
                  {draft.revenueShare.chargeBasis === 'partner_markup'
                    ? `Overture charges the partner $100.00. To preserve a ${Number(draft.revenueShare.overturePercent).toFixed(2)}/${(100 - Number(draft.revenueShare.overturePercent)).toFixed(2)} split, the partner could charge parents $${(100 / (Number(draft.revenueShare.overturePercent) / 100)).toFixed(2)} and keep the added margin.`
                    : `The partner charges parents $100.00. Overture charges the partner $${Number(draft.revenueShare.overturePercent).toFixed(2)}, and the partner keeps $${(100 - Number(draft.revenueShare.overturePercent)).toFixed(2)}.`}
                </span>
              </div>
            )}
          </div>
        )}
        <label className="partner-checkbox partner-billing-prorating"><input type="checkbox" checked={Boolean(draft.proratedAllowed)} onChange={(event) => setDraft({ ...draft, proratedAllowed: event.target.checked })} /> Prorating allowed by default</label></div>
      </details>
      <div className="partner-billing-autosave-footer"><span className={`partner-autosave-state partner-autosave-state--${autosaveState}`}>{autosaveLabel}</span>{billingSetupComplete && <button type="button" onClick={() => setSetupExpanded(false)}>Done editing</button>}</div>
      </>}

      <div className="partner-subsection" id="billing-documents">
      <h3>Partner documents</h3>
      <p className="partner-section-intro">COI, W-9, contracts, vendor documents, and partner-level safety protocols.</p>
      {uploadError && <div role="alert" className="partner-notice partner-notice--error">{uploadError}</div>}
      <form
        className="partner-inline-create partner-inline-create--document"
        onSubmit={async (event) => {
          event.preventDefault();
          if (!documentFile) return;
          setUploading(true);
          setUploadError(null);
          try {
            await uploadPartnerDocument({
              partnerId: partner.id,
              file: documentFile,
              kind: documentKind,
              title: documentTitle || documentFile.name,
              actorId,
            });
            setDocumentFile(null);
            setDocumentTitle('');
            await onChanged();
          } catch (nextError) {
            setUploadError(nextError instanceof Error ? nextError.message : 'Document upload failed.');
          } finally {
            setUploading(false);
          }
        }}
      >
        <select aria-label="Document type" value={documentKind} onChange={(event) => setDocumentKind(event.target.value as PartnerDocumentKind)}>
          <option value="coi">COI</option>
          <option value="w9">W-9</option>
          <option value="contract">Contract</option>
          <option value="vendor">Vendor process</option>
          <option value="safety_protocol">Safety protocol</option>
          <option value="other">Other</option>
        </select>
        <input aria-label="Document title" value={documentTitle} onChange={(event) => setDocumentTitle(event.target.value)} placeholder="Document title" />
        <input aria-label="Document file" type="file" accept=".pdf,image/*" onChange={(event) => setDocumentFile(event.target.files?.[0] || null)} required />
        <button type="submit" disabled={uploading}>{uploading ? 'Uploading…' : 'Upload'}</button>
      </form>
      {documents.length > 0 && (
        <table className="partner-data-table">
          <thead><tr><th>Document</th><th>Type</th><th>Uploaded</th><th>Effective</th><th>Expires</th><th>File</th></tr></thead>
          <tbody>
            {documents.map((document) => (
              <tr key={document.id}>
                <td><strong>{document.title}</strong>{document.description && <div className="partner-help-text">{document.description}</div>}</td>
                <td>{humanize(document.kind)}</td>
                <td>{formatDate(document.file.uploadedAt || document.createdAt) || 'Unknown'}</td>
                <td>{document.effectiveDate || '—'}</td>
                <td>{document.expirationDate || '—'}</td>
                <td><a href={document.file.url} target="_blank" rel="noreferrer">Open file</a></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {documents.length === 0 && <div className="partner-empty-state"><strong>No documents uploaded</strong><span>Add the COI, W-9, contract, or vendor documentation required for billing readiness.</span></div>}
      </div>
    </section>
  );
}

function SeasonsSection(props: {
  partner: Partner;
  seasons: PartnerSeason[];
  locations: PartnerLocation[];
  squads: Squad[];
  actorId: string | null;
  onChanged: () => Promise<void>;
  onCreateSquad: (termId: string, title: string, locationId: string) => Promise<string>;
}): React.ReactElement {
  const { partner, seasons, locations, squads, actorId, onChanged, onCreateSquad } = props;
  const [organizationSeasons, setOrganizationSeasons] = useState<OrganizationSeason[]>([]);
  const [expandedTermId, setExpandedTermId] = useState<string | null>(null);
  const [editRequest, setEditRequest] = useState<{ termId: string; token: number } | null>(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const year = new Date().getFullYear();
  const today = new Date().toISOString().slice(0, 10);
  const suggestedTerms = useMemo(() => listProgramTerms({ startYear: year - 1, years: 3 }), [year]);
  useEffect(() => {
    listOrganizationSeasons().then(setOrganizationSeasons).catch(() => setOrganizationSeasons([]));
  }, []);
  const orgSeason = (termId: string) => organizationSeasons.find((item) => item.termId === termId) || organizationSeasonForTerm(termId);
  const seasonEntries = useMemo(() => suggestedTerms.map((term) => {
    const saved = seasons.find((season) => season.termId === term.id || season.id === term.id) || null;
    const defaults = organizationSeasons.find((item) => item.termId === term.id) || organizationSeasonForTerm(term.id);
    const start = defaults?.defaultProgramStartDate || saved?.defaultProgramStartDate || '';
    const end = defaults?.defaultProgramEndDate || saved?.defaultProgramEndDate || '';
    const relation = start && end && today >= start && today <= end ? 'current' : start > today ? 'future' : end && end < today ? 'past' : 'future';
    return { term, saved, defaults, start, end, relation };
  }), [organizationSeasons, seasons, suggestedTerms, today]);
  const currentEntry = seasonEntries.find((entry) => entry.relation === 'current') || null;
  const nextEntry = seasonEntries.filter((entry) => entry.relation === 'future').sort((a, b) => a.start.localeCompare(b.start))[0] || null;
  const visibleEntryIds = new Set([currentEntry?.term.id, nextEntry?.term.id].filter(Boolean));
  const primaryEntries = [currentEntry, nextEntry].filter((entry): entry is NonNullable<typeof entry> => Boolean(entry));
  primaryEntries.sort((a, b) => {
    const aCommitted = Boolean(a.saved && ['verbal', 'written', 'contract'].includes(a.saved.commitment?.mode || ''));
    const bCommitted = Boolean(b.saved && ['verbal', 'written', 'contract'].includes(b.saved.commitment?.mode || ''));
    if (a.relation === 'future' && !aCommitted && !(b.relation === 'future' && !bCommitted)) return -1;
    if (b.relation === 'future' && !bCommitted && !(a.relation === 'future' && !aCommitted)) return 1;
    return a.start.localeCompare(b.start);
  });
  const laterEntries = seasonEntries.filter((entry) => entry.relation === 'future' && !visibleEntryIds.has(entry.term.id) && entry.saved);
  const pastEntries = seasonEntries.filter((entry) => entry.relation === 'past' && entry.saved).sort((a, b) => b.end.localeCompare(a.end));

  const setInitialCommitment = async (termId: string, mode: 'verbal' | 'declined') => {
    setSaving(true);
    setFormError(null);
    try {
      const defaults = orgSeason(termId);
      if (!defaults) throw new Error('No organization defaults exist for this season.');
      await savePartnerSeason(partner.id, termId, {
        termId,
        label: programTermLabelFromId(termId),
        commitment: { mode, statement: '', confirmedAt: mode === 'verbal' ? new Date() : undefined },
        planningPeriodStartDate: defaults.planningPeriodStartDate,
        planningPeriodEndDate: defaults.planningPeriodEndDate,
        registrationStartDate: defaults.registrationStartDate,
        registrationEndDate: defaults.registrationEndDate,
        defaultProgramStartDate: defaults.defaultProgramStartDate,
        defaultProgramEndDate: defaults.defaultProgramEndDate,
        dateSources: ORG_DATE_SOURCE,
        registrationLinkOverridesByLocationId: {}, linkedLocationIds: [], linkedSquadIds: [],
        createdBy: actorId, updatedBy: actorId,
      }, actorId);
      setExpandedTermId(termId);
      await onChanged();
    } catch (nextError) {
      setFormError(nextError instanceof Error ? nextError.message : 'Failed to update season commitment.');
    } finally {
      setSaving(false);
    }
  };

  const renderEntry = (entry: typeof seasonEntries[number]) => {
    const { saved, defaults, term, relation } = entry;
    const derived = saved ? derivePartnerSeason(saved, squads, new Date(), defaults) : null;
    const commitmentMode = saved?.commitment?.mode;
    const committed = Boolean(commitmentMode && ['verbal', 'written', 'contract'].includes(commitmentMode));
    const commitmentLabel = committed ? 'Committed' : commitmentMode === 'declined' ? 'Not moving forward' : !saved ? 'Not committed yet' : 'Needs classification';
    const expanded = expandedTermId === term.id;
    const nextLabel = derived?.nextAction?.label || (!saved || !committed ? `Confirm whether ${partner.name} will run ${term.label}` : 'No open action');
    const suggestedSquads = squads.filter((squad) => {
      if (['archived', 'canceled'].includes(squad.status || '')) return false;
      const assignedTermId = squad.termId || squad.seasonId || '';
      if (assignedTermId === term.id) return false;
      return inferSeasonFromStartDate(effectiveScheduleDate(squad), seasons)?.termId === term.id;
    });
    return <div key={term.id} className={`partner-season-accordion${expanded ? ' partner-season-accordion--open' : ''}`}>
      <div className="partner-season-accordion__header">
        <button type="button" className="partner-season-accordion__trigger" aria-expanded={expanded} onClick={() => setExpandedTermId(expanded ? null : term.id)}>
          <span className="partner-season-accordion__identity"><strong>{term.label}</strong><small>{relation === 'current' ? 'Current season' : relation === 'future' ? 'Next season' : 'Complete'}</small></span>
          <span className={`partner-commitment-badge partner-commitment-badge--${committed ? 'committed' : commitmentLabel === 'Needs classification' ? 'unknown' : 'not-committed'}`}>{commitmentLabel}</span>
          <span className="partner-season-accordion__phase">{derived ? seasonPhaseLabel(derived.phase) : 'Planning not started'}</span>
          {derived ? <span className={`partner-season-health partner-season-health--${derived.health}`}>{seasonHealthLabel(derived.health)}</span> : <span />}
          <span className="partner-season-accordion__readiness">{derived ? (derived.squads.length ? `${derived.readySquadCount}/${derived.squads.length} squads ready` : 'No squads entered') : 'Organization defaults available'}</span>
          <span className="partner-season-accordion__next"><small>Next</small>{nextLabel}</span>
          <span className="partner-season-accordion__chevron" aria-hidden="true">⌄</span>
        </button>
        {expanded && saved && <button type="button" className="partner-season-accordion__edit" onClick={() => setEditRequest({ termId: term.id, token: Date.now() })}>Edit season</button>}
      </div>
      {expanded && <div className="partner-season-accordion__body">
        {saved && derived ? <SeasonWorkspace partner={partner} season={saved} derived={derived} suggestedSquads={suggestedSquads} locations={locations} actorId={actorId} onChanged={onChanged} onCreateSquad={onCreateSquad} editRequestToken={editRequest?.termId === term.id ? editRequest.token : 0} /> : <div className="partner-season-proposal"><div><h3>Will {partner.name} run {term.label}?</h3><p>The season and organization dates are ready. Choosing a commitment state starts the appropriate workflow automatically.</p></div><div className="partner-season-date-readout"><span>Registration <strong>{defaults?.registrationStartDate} – {defaults?.registrationEndDate}</strong></span><span>Programs <strong>{defaults?.defaultProgramStartDate} – {defaults?.defaultProgramEndDate}</strong></span></div><div className="partner-actions"><button type="button" className="partner-primary-button" disabled={saving || !defaults} onClick={() => void setInitialCommitment(term.id, 'verbal')}>{saving ? 'Saving…' : 'Mark committed'}</button><button type="button" disabled={saving || !defaults} onClick={() => void setInitialCommitment(term.id, 'declined')}>Not moving forward</button></div></div>}
      </div>}
    </div>;
  };
  return (
    <section>
      <div className="partner-section-header">
        <div>
          <h2>Seasons</h2>
          <p>Shared planning scaffolding for each portfolio season. Squad schedules become operational truth once they exist.</p>
        </div>
      </div>
      {formError && <div role="alert" className="partner-notice partner-notice--error">{formError}</div>}
      <div className="partner-season-accordion-list">{primaryEntries.map(renderEntry)}</div>
      {laterEntries.length > 0 && <details className="partner-season-archive"><summary>Future seasons ({laterEntries.length})</summary><div className="partner-season-accordion-list">{laterEntries.map(renderEntry)}</div></details>}
      {pastEntries.length > 0 && <details className="partner-season-archive"><summary>Completed seasons ({pastEntries.length})</summary><div className="partner-season-accordion-list">{pastEntries.map(renderEntry)}</div></details>}
    </section>
  );
}

const TIMELINE_DAY_MS = 86400000;
const timelineDate = (value: string) => new Date(`${value}T00:00:00.000Z`);
const timelineYmd = (date: Date) => date.toISOString().slice(0, 10);
const shiftTimelineDate = (value: string, days: number) => timelineYmd(new Date(timelineDate(value).getTime() + days * TIMELINE_DAY_MS));

function EditableTimelineBand(props: {
  axisStart: string;
  axisEnd: string;
  startDate?: string | null;
  endDate?: string | null;
  label: string;
  className: string;
  disabled?: boolean;
  onChange: (startDate: string, endDate: string) => Promise<void> | void;
}): React.ReactElement {
  const { axisStart, axisEnd, startDate, endDate, label, className, disabled, onChange } = props;
  const trackRef = React.useRef<HTMLDivElement | null>(null);
  const [drag, setDrag] = useState<null | { kind: 'create' | 'start' | 'end' | 'move'; origin: string; initialStart: string; initialEnd: string; start: string; end: string }>(null);
  const axisDays = Math.max(1, Math.round((timelineDate(axisEnd).getTime() - timelineDate(axisStart).getTime()) / TIMELINE_DAY_MS));
  const pct = (value: string) => Math.max(0, Math.min(100, ((timelineDate(value).getTime() - timelineDate(axisStart).getTime()) / TIMELINE_DAY_MS / axisDays) * 100));
  const dateAt = (clientX: number) => {
    const rect = trackRef.current?.getBoundingClientRect();
    if (!rect) return axisStart;
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    return shiftTimelineDate(axisStart, Math.round(ratio * axisDays));
  };
  const begin = (event: React.PointerEvent, kind: 'create' | 'start' | 'end' | 'move') => {
    if (disabled) return;
    event.preventDefault();
    event.stopPropagation();
    const point = dateAt(event.clientX);
    const initialStart = startDate || point;
    const initialEnd = endDate || point;
    trackRef.current?.setPointerCapture(event.pointerId);
    setDrag({ kind, origin: point, initialStart, initialEnd, start: kind === 'create' ? point : initialStart, end: kind === 'create' ? point : initialEnd });
  };
  const move = (event: React.PointerEvent) => {
    if (!drag) return;
    const point = dateAt(event.clientX);
    let nextStart = drag.start;
    let nextEnd = drag.end;
    if (drag.kind === 'create') {
      nextStart = point < drag.origin ? point : drag.origin;
      nextEnd = point < drag.origin ? drag.origin : point;
    } else if (drag.kind === 'start') nextStart = point > drag.initialEnd ? drag.initialEnd : point;
    else if (drag.kind === 'end') nextEnd = point < drag.initialStart ? drag.initialStart : point;
    else {
      const offset = Math.round((timelineDate(point).getTime() - timelineDate(drag.origin).getTime()) / TIMELINE_DAY_MS);
      const duration = Math.round((timelineDate(drag.initialEnd).getTime() - timelineDate(drag.initialStart).getTime()) / TIMELINE_DAY_MS);
      nextStart = shiftTimelineDate(drag.initialStart, offset);
      nextEnd = shiftTimelineDate(drag.initialEnd, offset);
      if (nextStart < axisStart) { nextStart = axisStart; nextEnd = shiftTimelineDate(axisStart, duration); }
      if (nextEnd > axisEnd) { nextEnd = axisEnd; nextStart = shiftTimelineDate(axisEnd, -duration); }
    }
    setDrag({ ...drag, start: nextStart, end: nextEnd });
  };
  const finish = (event: React.PointerEvent) => {
    if (!drag) return;
    trackRef.current?.releasePointerCapture(event.pointerId);
    const { start, end } = drag;
    setDrag(null);
    void onChange(start, end);
  };
  const shownStart = drag?.start || startDate || null;
  const shownEnd = drag?.end || endDate || null;
  return <div ref={trackRef} className={`partner-season-timeline__track${disabled ? ' partner-season-timeline__track--locked' : ''}`} onPointerDown={(event) => { if (event.target === event.currentTarget) begin(event, 'create'); }} onPointerMove={move} onPointerUp={finish}>
    {shownStart && shownEnd ? <span className={`partner-season-timeline__band ${className}`} style={{ left: `${pct(shownStart)}%`, width: `${Math.max(1.2, pct(shownEnd) - pct(shownStart))}%` }} onPointerDown={(event) => begin(event, 'move')} title={`${shownStart} – ${shownEnd}`}><i className="partner-season-timeline__handle partner-season-timeline__handle--start" onPointerDown={(event) => begin(event, 'start')} />{label} · {shownStart} – {shownEnd}<i className="partner-season-timeline__handle partner-season-timeline__handle--end" onPointerDown={(event) => begin(event, 'end')} /></span> : <span className="partner-season-timeline__missing">Click and drag to add dates</span>}
  </div>;
}

function SeasonTimeline(props: { season: PartnerSeason; derived: DerivedPartnerSeason; onRangeChange: (kind: 'registration' | 'program', startDate: string, endDate: string) => Promise<void> }): React.ReactElement {
  const { season, derived, onRangeChange } = props;
  const today = timelineYmd(new Date());
  const datedValues = [
    derived.effectiveSeason.planningPeriodStartDate, derived.effectiveSeason.planningPeriodEndDate,
    derived.effectiveSeason.registrationStartDate, derived.effectiveSeason.registrationEndDate,
    derived.effectiveSeason.defaultProgramStartDate, derived.effectiveSeason.defaultProgramEndDate,
    ...derived.squads.flatMap((item) => [item.startDate, item.endDate]),
    ...derived.milestones.map((item) => item.date),
    today,
  ].filter((value): value is string => Boolean(value && /^\d{4}-\d{2}-\d{2}$/.test(value)));
  const rawStart = datedValues.slice().sort()[0] || today;
  const sortedDates = datedValues.slice().sort();
  const rawEnd = sortedDates[sortedDates.length - 1] || today;
  const startDate = new Date(timelineDate(rawStart));
  startDate.setUTCDate(1);
  const endDate = new Date(timelineDate(rawEnd));
  endDate.setUTCMonth(endDate.getUTCMonth() + 1, 0);
  const start = timelineYmd(startDate);
  const end = timelineYmd(endDate);
  const totalDays = Math.max(1, Math.round((timelineDate(end).getTime() - timelineDate(start).getTime()) / TIMELINE_DAY_MS));
  const pct = (value: string) => Math.max(0, Math.min(100, ((timelineDate(value).getTime() - timelineDate(start).getTime()) / TIMELINE_DAY_MS / totalDays) * 100));
  const bandStyle = (from: string, to: string) => ({ left: `${pct(from)}%`, width: `${Math.max(1.2, pct(to) - pct(from))}%` });
  const months: Array<{ date: string; label: string }> = [];
  const cursor = new Date(startDate);
  while (cursor <= endDate) {
    months.push({ date: timelineYmd(cursor), label: cursor.toLocaleString('en-US', { month: 'short', timeZone: 'UTC' }) });
    cursor.setUTCMonth(cursor.getUTCMonth() + 1, 1);
  }
  const confirmMilestones = derived.squads
    .filter((item) => item.startDate && item.scheduleStatus !== 'confirmed')
    .map((item) => ({ id: `confirm-${item.squad.id}`, label: `Confirm ${item.squad.title}`, date: shiftTimelineDate(item.startDate!, -28) }));
  const milestones = [...derived.milestones.filter((item): item is typeof item & { date: string } => Boolean(item.date)), ...confirmMilestones];
  const operatingStartItem = derived.squads.find((item) => item.startDate === derived.operatingStartDate);
  const operatingEndItem = derived.squads.find((item) => item.endDate === derived.operatingEndDate);
  const operatingBoundaryIsDraft = Boolean((operatingStartItem && operatingStartItem.scheduleStatus !== 'confirmed') || (operatingEndItem && operatingEndItem.scheduleStatus !== 'confirmed'));
  const milestoneLanes: number[] = [];
  const lastPositionByLane: number[] = [];
  milestones
    .map((milestone, index) => ({ index, position: pct(milestone.date) }))
    .sort((a, b) => a.position - b.position)
    .forEach(({ index, position }) => {
      let lane = lastPositionByLane.findIndex((last) => position - last >= 14);
      if (lane < 0) lane = lastPositionByLane.length;
      lastPositionByLane[lane] = position;
      milestoneLanes[index] = lane;
    });
  const milestoneHeight = Math.max(78, lastPositionByLane.length * 46 + 18);
  return <div className="partner-season-timeline" aria-label="Season timeline">
    <div className="partner-season-timeline__plot">
      <div className="partner-season-timeline__months">{months.map((month) => <span key={month.date} style={{ left: `${pct(month.date)}%` }}>{month.label}</span>)}</div>
      {pct(today) >= 0 && pct(today) <= 100 && <div className="partner-season-timeline__today"><i style={{ left: `${pct(today)}%` }}><span>Today · {today}</span></i></div>}
      <div className="partner-season-timeline__row"><strong>Planning</strong><div className="partner-season-timeline__track partner-season-timeline__track--locked">{derived.effectiveSeason.planningPeriodStartDate && derived.effectiveSeason.planningPeriodEndDate && <span className="partner-season-timeline__band partner-season-timeline__band--planning" style={bandStyle(derived.effectiveSeason.planningPeriodStartDate, derived.effectiveSeason.planningPeriodEndDate)}>Planning · {derived.effectiveSeason.planningPeriodStartDate} – {derived.effectiveSeason.planningPeriodEndDate}</span>}</div></div>
      <div className="partner-season-timeline__row"><strong>Registration</strong><EditableTimelineBand axisStart={start} axisEnd={end} startDate={derived.effectiveSeason.registrationStartDate} endDate={derived.effectiveSeason.registrationEndDate} label="Registration" className="partner-season-timeline__band--registration" onChange={(from, to) => onRangeChange('registration', from, to)} /></div>
      <div className="partner-season-timeline__row"><strong>{derived.squads.length ? 'Operating window' : 'Program plan'}</strong>{derived.squads.length ? <div className="partner-season-timeline__track partner-season-timeline__track--locked">{derived.operatingStartDate && derived.operatingEndDate && <span className={`partner-season-timeline__band partner-season-timeline__band--program${operatingBoundaryIsDraft ? ' partner-season-timeline__band--draft-boundary' : ''}`} style={bandStyle(derived.operatingStartDate, derived.operatingEndDate)}>Squad envelope · {derived.operatingStartDate} – {derived.operatingEndDate}</span>}{season.defaultProgramStartDate && season.defaultProgramEndDate && (season.defaultProgramStartDate !== derived.operatingStartDate || season.defaultProgramEndDate !== derived.operatingEndDate) && <span className="partner-season-timeline__planned-outline" style={bandStyle(season.defaultProgramStartDate, season.defaultProgramEndDate)} title={`Planned ${season.defaultProgramStartDate} – ${season.defaultProgramEndDate}`} />}</div> : <EditableTimelineBand axisStart={start} axisEnd={end} startDate={season.defaultProgramStartDate} endDate={season.defaultProgramEndDate} label="Program plan" className="partner-season-timeline__band--program" onChange={(from, to) => onRangeChange('program', from, to)} />}</div>
      <div className="partner-season-timeline__group-label">Squads</div>
      {derived.squads.map((item) => <div className="partner-season-timeline__row" key={item.squad.id}><strong title={item.squad.title}>{item.squad.title}</strong><div className="partner-season-timeline__track">{item.startDate && item.endDate ? <span title={`${item.squad.title} · ${item.startDate} – ${item.endDate}`} className={`partner-season-timeline__band partner-season-timeline__band--squad partner-season-timeline__band--${item.scheduleStatus === 'confirmed' ? 'confirmed' : 'draft'}`} style={bandStyle(item.startDate, item.endDate)}>{item.squad.title}</span> : <span className="partner-season-timeline__missing">Schedule dates missing</span>}</div></div>)}
      {derived.squads.length === 0 && <div className="partner-season-timeline__empty">No squads yet</div>}
      <div className="partner-season-timeline__group-label">Milestones</div>
      <div className="partner-season-timeline__milestones" style={{ height: milestoneHeight }}>{milestones.map((milestone, index) => <span key={milestone.id} className="partner-season-timeline__milestone" style={{ left: `${pct(milestone.date)}%`, top: `${7 + milestoneLanes[index] * 46}px` }} title={`${milestone.label} · ${milestone.date}`}><i /> <b>{milestone.label}</b><small>{milestone.date}</small></span>)}</div>
    </div>
    <div className="partner-season-timeline__key"><span><i className="is-confirmed" /> Confirmed</span><span><i className="is-draft" /> Draft</span><span><i className="is-milestone" /> Milestone</span></div>
  </div>;
}

function SeasonWorkspace(props: {
  partner: Partner;
  season: PartnerSeason;
  derived: DerivedPartnerSeason;
  suggestedSquads: Squad[];
  locations: PartnerLocation[];
  actorId: string | null;
  onChanged: () => Promise<void>;
  onCreateSquad: (termId: string, title: string, locationId: string) => Promise<string>;
  editRequestToken: number;
}): React.ReactElement {
  const navigate = useNavigate();
  const { partner, season, derived, suggestedSquads, locations, actorId, onChanged, onCreateSquad, editRequestToken } = props;
  const [draft, setDraft] = useState(season);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [creatingSquad, setCreatingSquad] = useState(false);
  const [newSquadTitle, setNewSquadTitle] = useState('');
  const [newSquadLocationId, setNewSquadLocationId] = useState(locations.length === 1 ? locations[0].id : '');
  const [createSquadError, setCreateSquadError] = useState<string | null>(null);
  const [movingSquadId, setMovingSquadId] = useState<string | null>(null);
  const committed = ['verbal', 'written', 'contract'].includes(season.commitment?.mode || '');
  useEffect(() => setDraft(season), [season]);
  useEffect(() => { if (editRequestToken) setEditing(true); }, [editRequestToken]);
  const marketing = marketingSummary(seasonMarketingDeliverables(season));
  const derivedLocationIds = Array.from(new Set(derived.squads.map((item) => item.squad.locationId).filter(Boolean))) as string[];
  const updateCommitment = async (mode: 'verbal' | 'declined' | null) => {
    setSaving(true);
    try {
      await savePartnerSeason(partner.id, season.id, {
        termId: season.termId,
        label: season.label,
        commitment: { ...season.commitment, mode, confirmedAt: mode === 'verbal' ? new Date() : null },
        createdBy: actorId,
        updatedBy: actorId,
      }, actorId);
      await onChanged();
    } finally {
      setSaving(false);
    }
  };
  const saveTimelineRange = async (kind: 'registration' | 'program', startDate: string, endDate: string) => {
    const patch = kind === 'registration'
      ? { registrationStartDate: startDate, registrationEndDate: endDate }
      : { defaultProgramStartDate: startDate, defaultProgramEndDate: endDate };
    const sourcePatch = kind === 'registration'
      ? { registrationStartDate: 'partner_override' as const, registrationEndDate: 'partner_override' as const }
      : { defaultProgramStartDate: 'partner_override' as const, defaultProgramEndDate: 'partner_override' as const };
    setDraft((current) => ({ ...current, ...patch, dateSources: { ...current.dateSources, ...sourcePatch } }));
    await savePartnerSeason(partner.id, season.id, { termId: season.termId, label: season.label, ...patch, dateSources: { ...season.dateSources, ...sourcePatch }, createdBy: actorId, updatedBy: actorId }, actorId);
    await onChanged();
  };
  const moveSuggestedSquad = async (squad: Squad) => {
    setMovingSquadId(squad.id);
    setCreateSquadError(null);
    try {
      await updatePartnerSquadSeason(
        partner.id,
        squad.id,
        squad.termId || squad.seasonId || '',
        season.termId,
        season.label || programTermLabelFromId(season.termId),
        squad.locationId || '',
        actorId,
      );
      await onChanged();
    } catch (nextError) {
      setCreateSquadError(nextError instanceof Error ? nextError.message : 'Unable to move squad into this season.');
    } finally {
      setMovingSquadId(null);
    }
  };
  return (
    <form
      className="partner-season-workspace"
      onSubmit={async (event) => {
        event.preventDefault();
        setSaving(true);
        try {
          await savePartnerSeason(partner.id, season.id, {
            termId: draft.termId,
            label: draft.label,
            commitment: draft.commitment,
            registrationStartDate: draft.registrationStartDate,
            registrationEndDate: draft.registrationEndDate,
            planningPeriodStartDate: draft.planningPeriodStartDate,
            planningPeriodEndDate: draft.planningPeriodEndDate,
            defaultProgramStartDate: draft.defaultProgramStartDate,
            defaultProgramEndDate: draft.defaultProgramEndDate,
            dateSources: draft.dateSources,
            registrationLinkOverridesByLocationId: draft.registrationLinkOverridesByLocationId || {},
            healthOverride: draft.healthOverride || null,
            linkedLocationIds: derivedLocationIds,
            createdBy: actorId,
            updatedBy: actorId,
          }, actorId);
          await onChanged();
          setEditing(false);
        } finally {
          setSaving(false);
        }
      }}
    >
      <div className="partner-season-commitment-prompt">
        <div>
          <strong>{committed ? 'Committed' : season.commitment?.mode === 'declined' ? 'Not moving forward' : 'Not committed yet'}</strong>
          <span>{committed ? 'This season is actively moving toward delivery.' : season.commitment?.mode === 'declined' ? 'This season is currently closed.' : 'Awaiting a commitment decision.'}</span>
        </div>
        <div className="partner-actions">
          {!committed && <button type="button" className="partner-primary-button" disabled={saving} onClick={() => void updateCommitment('verbal')}>Mark committed</button>}
          {(committed || season.commitment?.mode === 'declined') && <button type="button" disabled={saving} onClick={() => void updateCommitment(null)}>Move to not committed yet</button>}
          {season.commitment?.mode !== 'declined' && <button type="button" disabled={saving} onClick={() => void updateCommitment('declined')}>Not moving forward</button>}
        </div>
      </div>

      <SeasonTimeline season={draft} derived={derived} onRangeChange={saveTimelineRange} />

      {editing && <details className="partner-subsection partner-season-advanced">
        <summary>Advanced settings and health override</summary>
        <p className="partner-section-intro">Calculated health is the default. Use a temporary override only when the system lacks important context.</p>
        <div className="partner-field-grid partner-field-grid--three">
          <label className="partner-field">Override status<select value={draft.healthOverride?.status || ''} onChange={(event) => setDraft({
            ...draft,
            healthOverride: event.target.value
              ? { status: event.target.value as any, reason: draft.healthOverride?.reason || '', expiresOn: draft.healthOverride?.expiresOn || '' }
              : null,
          })}><option value="">Use calculated health</option><option value="on_track">On track</option><option value="attention">Attention</option><option value="at_risk">At risk</option><option value="blocked">Blocked</option></select></label>
          <label className="partner-field">Reason<input value={draft.healthOverride?.reason || ''} disabled={!draft.healthOverride} required={Boolean(draft.healthOverride)} onChange={(event) => setDraft({ ...draft, healthOverride: draft.healthOverride ? { ...draft.healthOverride, reason: event.target.value } : null })} /></label>
          <label className="partner-field">Revisit on<input type="date" value={draft.healthOverride?.expiresOn || ''} disabled={!draft.healthOverride} required={Boolean(draft.healthOverride)} onChange={(event) => setDraft({ ...draft, healthOverride: draft.healthOverride ? { ...draft.healthOverride, expiresOn: event.target.value } : null })} /></label>
        </div>
        {draft.healthOverride && <div className="partner-help-text">The override expires automatically; calculated health remains visible after it lapses.</div>}
      </details>}

      <details className="partner-subsection partner-season-setup-section" id="partner-season-dates" open={editing || undefined}>
        <summary>Dates and season setup</summary>
        <p className="partner-section-intro">These windows provide planning scaffolding. Actual squad schedules override the default program window.</p>
        {editing ? <div className="partner-field-grid">
        <label className="partner-field">Display label<input value={draft.label || ''} onChange={(event) => setDraft({ ...draft, label: event.target.value })} placeholder={humanize(draft.termId)} /></label>
        <label className="partner-field">Commitment mode<select value={draft.commitment?.mode || ''} onChange={(event) => setDraft({ ...draft, commitment: { ...draft.commitment, mode: (event.target.value || null) as any, confirmedAt: event.target.value ? draft.commitment?.confirmedAt : null } })}><option value="">Not committed yet</option><option value="verbal">Committed · verbal</option><option value="written">Committed · written</option><option value="contract">Committed · contract</option><option value="declined">Not moving forward</option></select></label>
        <label className="partner-field">Commitment statement<textarea value={draft.commitment?.statement || ''} onChange={(event) => setDraft({ ...draft, commitment: { ...draft.commitment, statement: event.target.value } })} /></label>
        {(['planningPeriodStartDate', 'planningPeriodEndDate', 'registrationStartDate', 'registrationEndDate', 'defaultProgramStartDate', 'defaultProgramEndDate'] as const).map((field) => <label key={field} className="partner-field">{humanize(field)}<input type="date" value={draft[field] || ''} onChange={(event) => setDraft({ ...draft, [field]: event.target.value, dateSources: { ...draft.dateSources, [field]: 'partner_override' } })} /><small>{draft.dateSources?.[field] === 'org_default' ? 'Organization default' : 'Partner adjustment'}</small></label>)}
        </div> : <div className="partner-season-date-readout"><span>Planning <strong>{derived.effectiveSeason.planningPeriodStartDate || 'Not set'} – {derived.effectiveSeason.planningPeriodEndDate || 'Not set'}</strong></span><span>Registration <strong>{derived.effectiveSeason.registrationStartDate || 'Not set'} – {derived.effectiveSeason.registrationEndDate || 'Not set'}</strong></span><span>Program window <strong>{derived.effectiveSeason.defaultProgramStartDate || 'Not set'} – {derived.effectiveSeason.defaultProgramEndDate || 'Not set'}</strong></span></div>}
      </details>

      <details className="partner-subsection partner-season-links-section">
        <summary>Registration links by active location</summary>
        <p className="partner-section-intro">Locations and squads are derived from each squad’s season assignment.</p>
        {derivedLocationIds.map((locationId) => {
          const location = locations.find((item) => item.id === locationId);
          return (
            <label key={locationId} className="partner-field" style={{ marginTop: 12 }}>
              Registration override · {location?.name || locationId}
              <input
                type="url"
                value={draft.registrationLinkOverridesByLocationId?.[locationId] || ''}
                onChange={(event) => setDraft({
                  ...draft,
                  registrationLinkOverridesByLocationId: {
                    ...(draft.registrationLinkOverridesByLocationId || {}),
                    [locationId]: event.target.value,
                  },
                })}
              />
            </label>
          );
        })}
        {derivedLocationIds.length === 0 && <div className="partner-help-text">No locations yet; they will appear when squads are assigned to this season.</div>}
      </details>

      <div className="partner-subsection partner-season-squad-section">
        <div className="partner-subsection__header"><h3>Squads in this season</h3>{committed && <button type="button" onClick={() => { setCreatingSquad((value) => !value); setCreateSquadError(null); }}>Create squad in {season.label || humanize(season.termId)}</button>}</div>
        {creatingSquad && <form className="partner-season-inline-create" onSubmit={async (event) => {
          event.preventDefault();
          if (!newSquadTitle.trim() || !newSquadLocationId) return;
          setSaving(true); setCreateSquadError(null);
          try {
            await onCreateSquad(season.termId, newSquadTitle.trim(), newSquadLocationId);
            setNewSquadTitle('');
            setCreatingSquad(false);
          } catch (nextError) {
            setCreateSquadError(nextError instanceof Error ? nextError.message : 'Unable to create squad.');
          } finally { setSaving(false); }
        }}>
          <label><span>Squad title</span><input autoFocus required maxLength={100} value={newSquadTitle} onChange={(event) => setNewSquadTitle(event.target.value)} placeholder={`e.g. ${partner.name} Intro to AI`} /></label>
          <label><span>Location</span><select required value={newSquadLocationId} onChange={(event) => setNewSquadLocationId(event.target.value)}><option value="">Choose location…</option>{locations.map((location) => <option key={location.id} value={location.id}>{location.name}</option>)}</select></label>
          <div><button type="submit" className="partner-primary-button" disabled={saving || !newSquadTitle.trim() || !newSquadLocationId}>{saving ? 'Creating…' : 'Create squad'}</button><button type="button" onClick={() => setCreatingSquad(false)} disabled={saving}>Cancel</button></div>
          {createSquadError && <p role="alert">{createSquadError}</p>}
        </form>}
        {derived.squads.length > 0 ? (
          <table className="partner-data-table">
            <thead><tr><th>Squad</th><th>Schedule dates</th><th>Confidence</th><th>Lead teacher</th><th>Health</th><th>Action</th></tr></thead>
            <tbody>{derived.squads.map((item) => (
              <tr
                key={item.squad.id}
                className="partner-season-squad-row"
                role="link"
                tabIndex={0}
                onClick={() => navigate(`/app/admin/squads/${item.squad.id}`)}
                onKeyDown={(event) => { if (event.key === 'Enter') navigate(`/app/admin/squads/${item.squad.id}`); }}
                aria-label={`Edit ${item.squad.title}`}
              >
                <td><strong className="partner-season-squad-link">{item.squad.title}</strong></td>
                <td>{item.squad.scheduleKind === 'single_day' || item.squad.schedule?.mainDate ? (item.startDate || 'Not set') : `${item.startDate || 'Not set'} – ${item.endDate || 'Not set'}`}</td>
                <td>{humanize(item.scheduleStatus)}</td>
                <td>{item.teacherReady ? 'Assigned' : 'Needed'}</td>
                <td><span className={`partner-season-health partner-season-health--${item.health}`}>{seasonHealthLabel(item.health)}</span><div className="partner-help-text">{item.reason}</div></td>
                <td><button type="button" className="partner-season-edit-squad" onClick={(event) => { event.stopPropagation(); navigate(`/app/admin/squads/${item.squad.id}`); }}>{item.scheduleStatus === 'proposed' ? 'Review proposal' : item.hasUsableSchedule ? 'Edit squad' : 'Complete schedule'}</button></td>
              </tr>
            ))}</tbody>
          </table>
        ) : <div className="partner-empty-state"><strong>No squads yet</strong><span>This is normal during planning. Assign a squad to {season.label || humanize(season.termId)} and it will appear automatically.</span></div>}
        {suggestedSquads.length > 0 && <div className="partner-season-suggestions">
          <div><h4>Suggested for {season.label || programTermLabelFromId(season.termId)}</h4><p>These squads are assigned elsewhere, but their first-class dates fall inside this season.</p></div>
          {suggestedSquads.map((squad) => {
            const currentTermId = squad.termId || squad.seasonId || '';
            const startDate = effectiveScheduleDate(squad);
            return <div className="partner-season-suggestion-row" key={squad.id}><span><strong>{squad.title}</strong><small>{startDate || 'Start date missing'} · currently {programTermLabelFromId(currentTermId) || 'unassigned'}</small></span><button type="button" disabled={Boolean(movingSquadId)} onClick={() => void moveSuggestedSquad(squad)}>{movingSquadId === squad.id ? 'Moving…' : `Move to ${season.label || programTermLabelFromId(season.termId)}`}</button></div>;
          })}
        </div>}
      </div>

      <div className="partner-subsection partner-season-marketing-section">
        <h3>Marketing</h3>
        <p>{marketing.newsletters} newsletters distributed · {marketing.newsletterOutreach} newsletter outreach touches · {marketing.open} open items</p>
        <button type="button" onClick={() => navigate(`/app/admin/partners/${partner.id}?tab=marketing&marketingSeason=${encodeURIComponent(season.id)}`)}>Open season marketing</button>
      </div>
      {editing && <div className="partner-actions">
        <button type="submit" disabled={saving}>{saving ? 'Saving season…' : 'Save season'}</button>
        <button type="button" onClick={() => { setDraft(season); setEditing(false); }}>Cancel</button>
      </div>}
    </form>
  );
}

function NotesSection(props: {
  partner: Partner;
  notes: ScopedNote[];
  actorId: string | null;
  onChanged: () => Promise<void>;
}): React.ReactElement {
  const { partner, notes, actorId, onChanged } = props;
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [category, setCategory] = useState('general');
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const needle = search.trim().toLowerCase();
  const bodyText = notePlainText(body);
  const visibleNotes = notes.filter((note) => !needle || [note.title, notePlainText(note.body), note.category].filter(Boolean).some((value) => String(value).toLowerCase().includes(needle)));
  return (
    <section className="partner-notes-page">
      <div className="partner-section-header">
        <div>
          <h2>Partner notes</h2>
          <p>Use this workspace for detailed context, decisions, meeting notes, and operational updates.</p>
        </div>
      </div>
      {formError && <div role="alert" className="partner-notice partner-notice--error">{formError}</div>}
      <form
        className="partner-note-composer"
        onSubmit={async (event) => {
          event.preventDefault();
          if (!bodyText) {
            setFormError('Write something in the note before saving.');
            return;
          }
          setSaving(true);
          setFormError(null);
          try {
            await createPartnerNote(partner.id, {
              title: title.trim() || null,
              body: sanitizeNoteHtml(body),
              category,
              createdBy: actorId,
              updatedBy: actorId,
            }, actorId);
            setTitle('');
            setBody('');
            await onChanged();
          } catch (nextError) {
            setFormError(nextError instanceof Error ? nextError.message : 'Failed to save note.');
          } finally {
            setSaving(false);
          }
        }}
      >
        <div className="partner-note-composer__header">
          <label className="partner-field">
            Note title
            <input value={title} onChange={(event) => setTitle(event.target.value)} placeholder="What is this note about?" />
          </label>
          <label className="partner-field">
            Category
            <select value={category} onChange={(event) => setCategory(event.target.value)}>
              <option value="general">General</option>
              <option value="operations">Operations</option>
              <option value="relationship">Relationship</option>
              <option value="billing">Billing</option>
              <option value="marketing">Marketing</option>
              <option value="risk">Risk</option>
            </select>
          </label>
        </div>
        <div className="partner-note-composer__editor">
          <HtmlDocumentEditor
            label="Note"
            value={body}
            onChange={setBody}
            minHeight={360}
            previewEnabled={false}
            placeholders={[]}
            paperMode={false}
            placeholder="Capture detailed context, decisions, follow-ups, meeting notes, or an important update…"
          />
        </div>
        <div className="partner-note-composer__footer">
          <span>{bodyText ? `${bodyText.split(/\s+/).length} words` : 'Start writing your note'}</span>
          <button type="submit" className="partner-primary-button" disabled={saving}>{saving ? 'Saving note…' : 'Save note'}</button>
        </div>
      </form>
      <div className="partner-subsection partner-note-history">
        <div className="partner-note-history__header">
          <div>
            <h3>Previous notes</h3>
            <p className="partner-section-intro">{notes.length} {notes.length === 1 ? 'note' : 'notes'} saved for this partner.</p>
          </div>
        </div>
        <input aria-label="Search notes" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search notes and categories…" />
        <div className="partner-note-feed">
          {visibleNotes.map((note) => (
            <article key={note.id} className="partner-note">
              <div>
                <strong>{note.title || humanize(note.category || 'General note')}</strong>{' '}
                <span className="partner-pill">{humanize(note.category || 'general')}</span>
              </div>
              <small>{formatDate(note.createdAt) || 'Date unavailable'}{note.createdBy ? ` · ${note.createdBy}` : ''}</small>
              <div
                className="partner-note__body"
                dangerouslySetInnerHTML={{ __html: noteDisplayHtml(note.body) }}
              />
            </article>
          ))}
        </div>
        {visibleNotes.length === 0 && <div className="partner-empty-state"><strong>{notes.length === 0 ? 'No notes yet' : 'No notes match this search'}</strong><span>{notes.length === 0 ? 'Capture the first operational update above.' : 'Try a different title, category, or phrase.'}</span></div>}
      </div>
    </section>
  );
}
